import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { 
  ArrowRight, ArrowLeft, BookOpen, Briefcase, Users, TrendingUp, GraduationCap, 
  DollarSign, CheckCircle, Scissors, UtensilsCrossed, Calendar,
  Trophy, Video, Image, Music, Upload, Phone, Mail, Play, X,
  Newspaper, Megaphone, Clock, Lock, Unlock, ChevronDown, CreditCard, IndianRupee,
  Eye, EyeOff, Gift, MessageCircle, Send, User, FileText, Plus, Trash2, Edit, 
  ClipboardList, CheckSquare, Star, AlertCircle, Settings, Sparkles, Banknote, Heart
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Import modular pages
import {
  Home,
  Login,
  Register,
  ForgotPassword,
  Activities,
  Competitions,
  Enrollment,
  DreamOption
} from "@/pages";

// Import layout components  
import { Navbar, Footer } from "@/components/layout";

import API from "@/utils/api";

// ============ DASHBOARD PAGE ============
const Dashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [activityPosts, setActivityPosts] = useState([]);
  const [districtMembers, setDistrictMembers] = useState([]);
  const [allMembers, setAllMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [editMemberData, setEditMemberData] = useState({ name: "", phone: "", email: "", district: "", user_type: "" });
  
  const districtColors = {
    "Headquarter": "bg-amber-100 text-amber-700 border-amber-300",
    "West Garo Hills": "bg-blue-100 text-blue-700 border-blue-300",
    "East Garo Hills": "bg-green-100 text-green-700 border-green-300",
    "North Garo Hills": "bg-purple-100 text-purple-700 border-purple-300",
    "South Garo Hills": "bg-pink-100 text-pink-700 border-pink-300",
    "South West Garo Hills": "bg-teal-100 text-teal-700 border-teal-300"
  };

  const districts = [
    "Headquarter",
    "West Garo Hills",
    "East Garo Hills",
    "North Garo Hills",
    "South Garo Hills",
    "South West Garo Hills"
  ];

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    const token = localStorage.getItem("token");
    
    if (!storedUser || !token) {
      navigate("/login");
      return;
    }
    
    try {
      const userData = JSON.parse(storedUser);
      setUser(userData);
      fetchActivityPosts();
      
      // Fetch members for co-admin or admin
      if (userData.role === "co_admin" || userData.role === "admin") {
        fetchMembers(token, userData);
      }
    } catch (e) {
      navigate("/login");
    }
  }, [navigate]);

  const fetchMembers = async (token, userData) => {
    setLoadingMembers(true);
    try {
      if (userData.role === "co_admin") {
        // Co-admin sees their district members
        const res = await axios.get(`${API}/co-admin/students`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDistrictMembers(res.data.students || []);
      } else if (userData.role === "admin") {
        // Admin sees all members
        const res = await axios.get(`${API}/admin/students`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Sort by date (newest first)
        const sorted = (res.data.students || []).sort((a, b) => 
          new Date(b.created_at) - new Date(a.created_at)
        );
        setAllMembers(sorted);
      }
    } catch (error) {
      console.error("Error fetching members:", error);
    } finally {
      setLoadingMembers(false);
    }
  };

  const openEditMember = (member) => {
    setEditingMember(member);
    setEditMemberData({
      name: member.name || "",
      phone: member.phone || "",
      email: member.email || "",
      district: member.district || "",
      user_type: member.user_type || "student"
    });
  };

  const saveMemberChanges = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");
    try {
      await axios.patch(`${API}/admin/members/${editingMember.id}`, editMemberData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingMember(null);
      fetchMembers(token, user);
      alert("Member updated successfully!");
    } catch (error) {
      alert(error.response?.data?.detail || "Error updating member");
    }
  };

  const deleteMember = async (memberId, memberName) => {
    if (!window.confirm(`Are you sure you want to delete "${memberName}"? This action cannot be undone.`)) return;
    const token = localStorage.getItem("token");
    try {
      await axios.delete(`${API}/admin/members/${memberId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchMembers(token, user);
      alert("Member deleted successfully!");
    } catch (error) {
      alert(error.response?.data?.detail || "Error deleting member");
    }
  };

  const fetchActivityPosts = async () => {
    try {
      const res = await axios.get(`${API}/activity-posts`);
      setActivityPosts(res.data.posts || []);
    } catch (error) {
      console.error("Error fetching activity posts:", error);
    }
  };
  
  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/");
  };

  const getCategoryIcon = (category) => {
    switch(category) {
      case "valentines": return <Heart className="w-4 h-4 text-pink-500" />;
      case "womens_day": return <Users className="w-4 h-4 text-purple-500" />;
      case "weekly_update": return <Calendar className="w-4 h-4 text-blue-500" />;
      default: return <Newspaper className="w-4 h-4 text-amber-500" />;
    }
  };

  const getCategoryLabel = (category) => {
    switch(category) {
      case "valentines": return "Valentine's Day";
      case "womens_day": return "Women's Day";
      case "weekly_update": return "Weekly Update";
      default: return "General";
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-transparent" data-testid="dashboard-page">
      <Navbar />
      <div className="pt-24 pb-16 max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-800">Welcome, {user.name}!</h1>
            <div className="text-slate-600 text-sm flex items-center flex-wrap gap-1">
              {user.district && <Badge className="mr-2 bg-teal-100 text-teal-700">{user.district}</Badge>}
              <span>{user.phone} • {user.email}</span>
            </div>
          </div>
          <div className="flex gap-2">
            <Link to="/chat">
              <Button className="rounded-full bg-teal-600 hover:bg-teal-700" data-testid="dashboard-chat-btn">
                <MessageCircle className="w-4 h-4 mr-2" />
                Messages
              </Button>
            </Link>
            <Button onClick={handleLogout} variant="outline" className="rounded-full" data-testid="dashboard-logout-btn">
              <X className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Link to="/chat">
            <Card className="rounded-2xl hover:shadow-lg transition-shadow cursor-pointer border-teal-200 bg-gradient-to-br from-teal-50 to-white">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                  <MessageCircle className="w-6 h-6 text-teal-600" />
                </div>
                <div>
                  <p className="font-bold text-slate-800">Chat</p>
                  <p className="text-sm text-slate-500">Message your {user.role === "co_admin" ? "students" : "co-admin"}</p>
                </div>
              </CardContent>
            </Card>
          </Link>
          
          {/* Enrollment for Dropouts */}
          {user.user_type === "girl" && (
            <Link to="/enrollment">
              <Card className={`rounded-2xl hover:shadow-lg transition-shadow cursor-pointer ${user.enrollment_submitted ? 'border-green-200 bg-gradient-to-br from-green-50 to-white' : 'border-amber-200 bg-gradient-to-br from-amber-50 to-white'}`}>
                <CardContent className="p-6 flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${user.enrollment_submitted ? 'bg-green-100' : 'bg-amber-100'}`}>
                    <FileText className={`w-6 h-6 ${user.enrollment_submitted ? 'text-green-600' : 'text-amber-600'}`} />
                  </div>
                  <div>
                    <p className="font-bold text-slate-800">Enrollment</p>
                    <p className="text-sm text-slate-500">{user.enrollment_submitted ? 'View documents' : 'Submit documents'}</p>
                  </div>
                </CardContent>
              </Card>
            </Link>
          )}

          {/* Dream Option for Educated Unemployed */}
          {user.user_type === "woman" && (
            <Link to="/dream-option">
              <Card className={`rounded-2xl hover:shadow-lg transition-shadow cursor-pointer ${user.dream_submitted ? 'border-green-200 bg-gradient-to-br from-green-50 to-white' : 'border-terracotta/30 bg-gradient-to-br from-terracotta/5 to-white'}`}>
                <CardContent className="p-6 flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${user.dream_submitted ? 'bg-green-100' : 'bg-terracotta/20'}`}>
                    <TrendingUp className={`w-6 h-6 ${user.dream_submitted ? 'text-green-600' : 'text-terracotta'}`} />
                  </div>
                  <div>
                    <p className="font-bold text-slate-800">Your Dream</p>
                    <p className="text-sm text-slate-500">{user.dream_submitted ? (user.dream_option === 'employed' ? 'Employment' : 'Self-Employment') : 'Choose your path'}</p>
                  </div>
                </CardContent>
              </Card>
            </Link>
          )}

          <Link to="/courses">
            <Card className="rounded-2xl hover:shadow-lg transition-shadow cursor-pointer">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="font-bold text-slate-800">Courses</p>
                  <p className="text-sm text-slate-500">Learn & grow</p>
                </div>
              </CardContent>
            </Card>
          </Link>
          <Link to="/competitions">
            <Card className="rounded-2xl hover:shadow-lg transition-shadow cursor-pointer">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <Trophy className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="font-bold text-slate-800">Competitions</p>
                  <p className="text-sm text-slate-500">Join contests</p>
                </div>
              </CardContent>
            </Card>
          </Link>
          <Link to="/payment">
            <Card className="rounded-2xl hover:shadow-lg transition-shadow cursor-pointer">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="font-bold text-slate-800">Payment</p>
                  <p className="text-sm text-slate-500">Donate & support</p>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Activities Corner */}
        <Card className="rounded-2xl mb-8">
          <CardHeader className="border-b">
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              Activities Corner
            </CardTitle>
            <CardDescription>Latest updates, events, and announcements</CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            {activityPosts.length === 0 ? (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No activities posted yet</p>
                <p className="text-sm text-slate-400">Check back later for updates!</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {activityPosts.map((post) => (
                  <Card key={post.id} className="rounded-xl overflow-hidden" data-testid={`activity-post-${post.id}`}>
                    {post.media_data && (
                      <div className="aspect-video bg-slate-100 overflow-hidden">
                        {post.media_type === "video" ? (
                          <video src={post.media_data} controls className="w-full h-full object-cover" />
                        ) : (
                          <img src={post.media_data} alt={post.title} className="w-full h-full object-cover" />
                        )}
                      </div>
                    )}
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        {getCategoryIcon(post.category)}
                        <Badge variant="outline" className="text-xs">{getCategoryLabel(post.category)}</Badge>
                      </div>
                      <h4 className="font-semibold text-slate-800 mb-1">{post.title}</h4>
                      <p className="text-sm text-slate-600 line-clamp-3">{post.content}</p>
                      <p className="text-xs text-slate-400 mt-2">
                        {new Date(post.created_at).toLocaleDateString()}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Registered Members Section - For Co-Admin */}
        {user.role === "co_admin" && (
          <Card className="rounded-2xl mb-8">
            <CardHeader className="border-b">
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-teal-600" />
                {user.district} - Registered Members
              </CardTitle>
              <CardDescription>{districtMembers.length} member(s) in your district</CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              {loadingMembers ? (
                <div className="text-center py-8">
                  <div className="w-8 h-8 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
                  <p className="text-slate-500 mt-2">Loading members...</p>
                </div>
              ) : districtMembers.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No members registered in your district yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="text-left py-3 px-3">Name</th>
                        <th className="text-left py-3 px-3">Phone</th>
                        <th className="text-left py-3 px-3">Type</th>
                        <th className="text-left py-3 px-3">Registered</th>
                        <th className="text-left py-3 px-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {districtMembers.map((member) => (
                        <tr key={member.id} className="border-b hover:bg-slate-50">
                          <td className="py-3 px-3">
                            <div>
                              <p className="font-medium">{member.name}</p>
                              <p className="text-xs text-slate-500">{member.email}</p>
                            </div>
                          </td>
                          <td className="py-3 px-3">{member.phone}</td>
                          <td className="py-3 px-3">
                            <Badge className={`text-xs ${
                              member.user_type === 'woman' ? 'bg-pink-100 text-pink-700' :
                              member.user_type === 'girl' ? 'bg-purple-100 text-purple-700' :
                              'bg-blue-100 text-blue-700'
                            }`}>
                              {member.user_type === 'woman' ? 'Educated Unemployed' : 
                               member.user_type === 'girl' ? 'Dropout' : 'General'}
                            </Badge>
                          </td>
                          <td className="py-3 px-3 text-slate-500 text-xs">
                            {new Date(member.created_at).toLocaleDateString()}
                          </td>
                          <td className="py-3 px-3">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => openEditMember(member)}
                                className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-lg"
                                title="Edit member"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => deleteMember(member.id, member.name)}
                                className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"
                                title="Delete member"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Registered Members Section - For Admin */}
        {user.role === "admin" && (
          <Card className="rounded-2xl mb-8">
            <CardHeader className="border-b">
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-600" />
                All Registered Members
              </CardTitle>
              <CardDescription>{allMembers.length} total member(s) - sorted by registration date</CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              {loadingMembers ? (
                <div className="text-center py-8">
                  <div className="w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
                  <p className="text-slate-500 mt-2">Loading members...</p>
                </div>
              ) : allMembers.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No members registered yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-white">
                      <tr className="border-b bg-slate-50">
                        <th className="text-left py-3 px-3">Name</th>
                        <th className="text-left py-3 px-3">Phone</th>
                        <th className="text-left py-3 px-3">Type</th>
                        <th className="text-left py-3 px-3">District</th>
                        <th className="text-left py-3 px-3">Registered</th>
                        <th className="text-left py-3 px-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allMembers.map((member) => (
                        <tr key={member.id} className="border-b hover:bg-slate-50">
                          <td className="py-3 px-3">
                            <div>
                              <p className="font-medium">{member.name}</p>
                              <p className="text-xs text-slate-500">{member.email}</p>
                            </div>
                          </td>
                          <td className="py-3 px-3">{member.phone}</td>
                          <td className="py-3 px-3">
                            <Badge className={`text-xs ${
                              member.user_type === 'woman' ? 'bg-pink-100 text-pink-700' :
                              member.user_type === 'girl' ? 'bg-purple-100 text-purple-700' :
                              'bg-blue-100 text-blue-700'
                            }`}>
                              {member.user_type === 'woman' ? 'Educated Unemployed' : 
                               member.user_type === 'girl' ? 'Dropout' : 'General'}
                            </Badge>
                          </td>
                          <td className="py-3 px-3">
                            <Badge className={`text-xs border ${districtColors[member.district] || 'bg-slate-100 text-slate-700'}`}>
                              {member.district}
                            </Badge>
                          </td>
                          <td className="py-3 px-3 text-slate-500 text-xs">
                            {new Date(member.created_at).toLocaleDateString()}
                          </td>
                          <td className="py-3 px-3">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => openEditMember(member)}
                                className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-lg"
                                title="Edit member"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => deleteMember(member.id, member.name)}
                                className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"
                                title="Delete member"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <div className="grid md:grid-cols-3 gap-6">
          <Link to="/courses">
            <Card className="rounded-2xl hover:shadow-lg transition-shadow cursor-pointer h-full">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-teal-600" />
                  My Courses
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600">Explore and enroll in courses to start learning.</p>
                <Button className="mt-3 bg-teal-600 hover:bg-teal-700 rounded-full" size="sm">
                  Browse Courses
                </Button>
              </CardContent>
            </Card>
          </Link>
          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-amber-600" />
                Progress
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600">Track your learning journey here.</p>
              <p className="text-sm text-slate-400 mt-2">Coming soon...</p>
            </CardContent>
          </Card>
          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-purple-600" />
                Resources
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600">Access financial support and resources.</p>
              <p className="text-sm text-slate-400 mt-2">Coming soon...</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Edit Member Modal */}
      {editingMember && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md rounded-2xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Edit className="w-5 h-5 text-teal-600" />
                Edit Member
              </CardTitle>
              <CardDescription>Update member details</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={saveMemberChanges} className="space-y-4">
                <div className="space-y-2">
                  <Label>Full Name</Label>
                  <Input
                    placeholder="Member's full name"
                    value={editMemberData.name}
                    onChange={(e) => setEditMemberData({...editMemberData, name: e.target.value})}
                    className="rounded-xl"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    placeholder="Phone number"
                    value={editMemberData.phone}
                    onChange={(e) => setEditMemberData({...editMemberData, phone: e.target.value})}
                    className="rounded-xl"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    placeholder="email@example.com"
                    value={editMemberData.email}
                    onChange={(e) => setEditMemberData({...editMemberData, email: e.target.value})}
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label>District</Label>
                  <select
                    value={editMemberData.district}
                    onChange={(e) => setEditMemberData({...editMemberData, district: e.target.value})}
                    className="w-full p-3 border rounded-xl bg-white"
                    required
                  >
                    <option value="">Select district</option>
                    {districts.map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Type</Label>
                  <select
                    value={editMemberData.user_type}
                    onChange={(e) => setEditMemberData({...editMemberData, user_type: e.target.value})}
                    className="w-full p-3 border rounded-xl bg-white"
                    required
                  >
                    <option value="woman">Educated Unemployed</option>
                    <option value="girl">Dropout</option>
                    <option value="general">General</option>
                  </select>
                </div>
                <div className="flex gap-3 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1 rounded-full"
                    onClick={() => setEditingMember(null)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 bg-teal-600 hover:bg-teal-700 rounded-full">
                    Save Changes
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// ============ CHAT PAGE ============
const Chat = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [mediaType, setMediaType] = useState(null);
  const [uploading, setUploading] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    const token = localStorage.getItem("token");
    
    if (!storedUser || !token) {
      navigate("/login");
      return;
    }
    
    try {
      const userData = JSON.parse(storedUser);
      setUser(userData);
      fetchConversations(token);
      fetchContacts(token, userData.role);
    } catch (e) {
      navigate("/login");
    }
  }, [navigate]);

  const fetchConversations = async (token) => {
    try {
      const response = await axios.get(`${API}/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error("Error fetching conversations:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async (token, role) => {
    try {
      if (role === "co_admin") {
        const response = await axios.get(`${API}/co-admin/students`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setContacts(response.data.students || []);
      } else if (role === "student" || role === "member") {
        const response = await axios.get(`${API}/student/co-admin`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.co_admin) {
          setContacts([response.data.co_admin]);
        }
      }
    } catch (error) {
      console.error("Error fetching contacts:", error);
    }
  };

  const fetchMessages = async (partnerId) => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(`${API}/messages/${partnerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data.messages || []);
      scrollToBottom();
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  const selectChat = (partner) => {
    setSelectedChat(partner);
    fetchMessages(partner.partner_id || partner.id);
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if ((!newMessage.trim() && !mediaFile) || !selectedChat) return;

    const token = localStorage.getItem("token");
    const receiverId = selectedChat.partner_id || selectedChat.id;

    setUploading(true);
    try {
      await axios.post(`${API}/messages`, {
        receiver_id: receiverId,
        content: newMessage || (mediaType === "image" ? "📷 Photo" : "🎬 Video"),
        media_data: mediaPreview,
        media_type: mediaType
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNewMessage("");
      setMediaFile(null);
      setMediaPreview(null);
      setMediaType(null);
      fetchMessages(receiverId);
      fetchConversations(token);
    } catch (error) {
      console.error("Error sending message:", error);
      alert("Failed to send message");
    } finally {
      setUploading(false);
    }
  };

  const handleMediaSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert("File size must be less than 5MB");
        return;
      }
      
      setMediaFile(file);
      const type = file.type.startsWith("video") ? "video" : "image";
      setMediaType(type);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setMediaPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const clearMedia = () => {
    setMediaFile(null);
    setMediaPreview(null);
    setMediaType(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const deleteMessage = async (messageId) => {
    if (!window.confirm("Are you sure you want to delete this message?")) return;
    
    const token = localStorage.getItem("token");
    try {
      await axios.delete(`${API}/messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchMessages(selectedChat.partner_id || selectedChat.id);
    } catch (error) {
      console.error("Error deleting message:", error);
      alert("Failed to delete message");
    }
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  // Auto-refresh messages every 5 seconds
  useEffect(() => {
    if (selectedChat) {
      const interval = setInterval(() => {
        fetchMessages(selectedChat.partner_id || selectedChat.id);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [selectedChat]);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-transparent" data-testid="chat-page">
      <Navbar />
      <div className="pt-20 h-screen flex flex-col">
        <div className="flex-1 max-w-6xl mx-auto w-full px-4 py-4 flex gap-4 overflow-hidden">
          {/* Sidebar - Conversations List */}
          <div className="w-full sm:w-80 flex-shrink-0 flex flex-col bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="p-4 border-b bg-gradient-to-r from-teal-600 to-teal-700 text-white">
              <h2 className="font-bold text-lg flex items-center gap-2">
                <MessageCircle className="w-5 h-5" />
                Messages
              </h2>
              <p className="text-sm text-teal-100">
                {user.role === "co_admin" ? `${user.district} Students` : "Your District Co-Admin"}
              </p>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              {/* Contacts/New Chats */}
              {contacts.length > 0 && (
                <div className="p-2 border-b">
                  <p className="text-xs text-slate-500 px-2 py-1">
                    {user.role === "co_admin" ? "Students in your district" : "Contact"}
                  </p>
                  {contacts.map((contact) => {
                    const existingConv = conversations.find(c => c.partner_id === contact.id);
                    if (existingConv) return null;
                    return (
                      <button
                        key={contact.id}
                        onClick={() => selectChat({ ...contact, partner_id: contact.id, partner_name: contact.name })}
                        className={`w-full p-3 flex items-center gap-3 hover:bg-slate-50 rounded-xl transition-colors ${
                          selectedChat?.id === contact.id ? 'bg-teal-50' : ''
                        }`}
                      >
                        <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-teal-600" />
                        </div>
                        <div className="text-left flex-1">
                          <p className="font-medium text-sm">{contact.name}</p>
                          <p className="text-xs text-slate-500">{contact.district || contact.role}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
              
              {/* Existing Conversations */}
              {conversations.length > 0 && (
                <div className="p-2">
                  <p className="text-xs text-slate-500 px-2 py-1">Recent Chats</p>
                  {conversations.map((conv) => (
                    <button
                      key={conv.partner_id}
                      onClick={() => selectChat(conv)}
                      className={`w-full p-3 flex items-center gap-3 hover:bg-slate-50 rounded-xl transition-colors ${
                        selectedChat?.partner_id === conv.partner_id ? 'bg-teal-50' : ''
                      }`}
                    >
                      <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center text-white font-bold">
                        {conv.partner_name?.charAt(0).toUpperCase()}
                      </div>
                      <div className="text-left flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-sm truncate">{conv.partner_name}</p>
                          {conv.unread_count > 0 && (
                            <Badge className="bg-teal-500 text-white text-xs">{conv.unread_count}</Badge>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 truncate">{conv.last_message}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {loading && (
                <div className="p-8 text-center text-slate-500">Loading...</div>
              )}

              {!loading && conversations.length === 0 && contacts.length === 0 && (
                <div className="p-8 text-center text-slate-500">
                  <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>No conversations yet</p>
                </div>
              )}
            </div>
          </div>

          {/* Chat Area */}
          <div className={`flex-1 flex flex-col bg-white rounded-2xl shadow-lg overflow-hidden ${!selectedChat && 'hidden sm:flex'}`}>
            {selectedChat ? (
              <>
                {/* Chat Header */}
                <div className="p-4 border-b bg-gradient-to-r from-slate-50 to-slate-100 flex items-center gap-3">
                  <button 
                    onClick={() => setSelectedChat(null)}
                    className="sm:hidden p-2 hover:bg-slate-200 rounded-full"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center text-white font-bold">
                    {selectedChat.partner_name?.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-bold">{selectedChat.partner_name}</p>
                    <p className="text-xs text-slate-500">{selectedChat.partner_role || "Student"}</p>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
                  {messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.sender_id === user.id ? 'justify-end' : 'justify-start'} group`}
                    >
                      <div
                        className={`max-w-[75%] p-3 rounded-2xl relative ${
                          msg.sender_id === user.id
                            ? 'bg-teal-600 text-white rounded-br-sm'
                            : 'bg-white shadow-sm rounded-bl-sm'
                        }`}
                      >
                        {/* Media Content */}
                        {msg.media_data && msg.media_type === "image" && (
                          <img 
                            src={msg.media_data} 
                            alt="Shared" 
                            className="max-w-full rounded-lg mb-2 cursor-pointer"
                            onClick={() => window.open(msg.media_data, '_blank')}
                          />
                        )}
                        {msg.media_data && msg.media_type === "video" && (
                          <video 
                            src={msg.media_data} 
                            controls 
                            className="max-w-full rounded-lg mb-2"
                          />
                        )}
                        {/* Text Content */}
                        {msg.content && msg.content !== "📷 Photo" && msg.content !== "🎬 Video" && (
                          <p className="text-sm">{msg.content}</p>
                        )}
                        <p className={`text-xs mt-1 ${msg.sender_id === user.id ? 'text-teal-200' : 'text-slate-400'}`}>
                          {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                        {/* Delete button for admin/co-admin */}
                        {(user.role === "admin" || user.role === "co_admin") && (
                          <button
                            onClick={() => deleteMessage(msg.id)}
                            className={`absolute -top-2 -right-2 p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity ${
                              msg.sender_id === user.id ? 'bg-red-500 text-white' : 'bg-red-500 text-white'
                            }`}
                            title="Delete message"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                {/* Message Input */}
                <div className="p-4 border-t bg-white">
                  {/* Media Preview */}
                  {mediaPreview && (
                    <div className="mb-3 relative inline-block">
                      {mediaType === "image" ? (
                        <img src={mediaPreview} alt="Preview" className="h-20 rounded-lg" />
                      ) : (
                        <video src={mediaPreview} className="h-20 rounded-lg" />
                      )}
                      <button
                        onClick={clearMedia}
                        className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                  <form onSubmit={sendMessage} className="flex gap-2">
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept="image/*,video/*"
                      onChange={handleMediaSelect}
                      className="hidden"
                      data-testid="chat-file-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      className="rounded-full px-3"
                      disabled={uploading}
                      data-testid="chat-attach-btn"
                    >
                      <Image className="w-5 h-5 text-slate-500" />
                    </Button>
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type a message..."
                      className="flex-1 rounded-full"
                      data-testid="chat-input"
                    />
                    <Button 
                      type="submit" 
                      className="rounded-full bg-teal-600 hover:bg-teal-700 px-4"
                      disabled={(!newMessage.trim() && !mediaFile) || uploading}
                      data-testid="chat-send-btn"
                    >
                      {uploading ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Send className="w-5 h-5" />
                      )}
                    </Button>
                  </form>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-slate-400">
                <div className="text-center">
                  <MessageCircle className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg">Select a conversation</p>
                  <p className="text-sm">Choose from the list to start chatting</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ DUNA 100 ADMIN COMPONENT ============
const Duna100Admin = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState({
    first: "",
    second: "",
    third: "",
    consolations: ["", "", "", "", "", "", "", "", "", ""],
    first_bonus: "",
    second_bonus: "",
    third_bonus: "",
    consolation_bonuses: ["", "", "", "", "", "", "", "", "", ""]
  });
  const [currentResults, setCurrentResults] = useState(null);

  useEffect(() => {
    fetchEntries();
    fetchCurrentResults();
  }, []);

  const fetchEntries = async () => {
    try {
      const response = await axios.get(`${API}/duna100-entries/export`);
      setEntries(response.data.entries || []);
    } catch (error) {
      console.error("Error fetching entries:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentResults = async () => {
    try {
      const response = await axios.get(`${API}/duna100-results/latest`);
      if (response.data) {
        setCurrentResults(response.data);
      }
    } catch (error) {
      console.error("Error fetching results:", error);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Are you sure you want to reset all sales for a new draw? This action cannot be undone.")) {
      return;
    }
    try {
      await axios.post(`${API}/duna100-entries/reset`);
      await axios.delete(`${API}/duna100-results`);
      fetchEntries();
      fetchCurrentResults();
      alert("Sales reset successfully. Ready for new draw!");
    } catch (error) {
      console.error("Error resetting:", error);
      alert("Failed to reset sales.");
    }
  };

  const handleSetResults = async () => {
    if (!results.first || !results.second || !results.third) {
      alert("Please enter at least First, Second, and Third prize numbers");
      return;
    }
    try {
      const consolationsList = results.consolations.filter(c => c.length === 2);
      const consolationBonusesList = results.consolation_bonuses.filter(b => b.length === 5);
      await axios.post(`${API}/duna100-results`, {
        first: results.first,
        second: results.second,
        third: results.third,
        consolations: consolationsList,
        first_bonus: results.first_bonus,
        second_bonus: results.second_bonus,
        third_bonus: results.third_bonus,
        consolation_bonuses: consolationBonusesList
      });
      fetchCurrentResults();
      alert("Results set successfully!");
    } catch (error) {
      console.error("Error setting results:", error);
      alert("Failed to set results.");
    }
  };

  const downloadReport = () => {
    if (entries.length === 0) {
      alert("No entries to export");
      return;
    }
    
    // Create CSV content
    let csv = "Name,Phone,Number,Amount,Agent,Date\n";
    let totalAmount = 0;
    entries.forEach(e => {
      csv += `"${e.name}","${e.phone}","${e.number}",${e.amount},"${e.agent}","${e.date}"\n`;
      totalAmount += e.amount;
    });
    csv += `\nTotal Entries,${entries.length}\nTotal Amount,${totalAmount}\n`;
    
    // Download
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `duna100_sales_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const updateConsolation = (index, value) => {
    const newConsolations = [...results.consolations];
    newConsolations[index] = value;
    setResults({ ...results, consolations: newConsolations });
  };

  const updateConsolationBonus = (index, value) => {
    const newBonuses = [...results.consolation_bonuses];
    newBonuses[index] = value;
    setResults({ ...results, consolation_bonuses: newBonuses });
  };

  const totalAmount = entries.reduce((sum, e) => sum + e.amount, 0);

  return (
    <div className="space-y-6">
      {/* Summary & Actions */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="rounded-2xl">
          <CardContent className="p-6 text-center">
            <p className="text-4xl font-bold text-pink-600">{entries.length}</p>
            <p className="text-slate-500">Total Entries</p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl">
          <CardContent className="p-6 text-center">
            <p className="text-4xl font-bold text-green-600">₹{totalAmount}</p>
            <p className="text-slate-500">Total Amount</p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl">
          <CardContent className="p-6 flex flex-col gap-2">
            <Button onClick={downloadReport} className="w-full" variant="outline" data-testid="export-btn">
              Download Report (CSV)
            </Button>
            <Button onClick={handleReset} className="w-full" variant="destructive" data-testid="reset-btn">
              Reset for New Draw
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Set Results */}
      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle>Set Results</CardTitle>
          <CardDescription>Enter winning 2-digit numbers and 5-digit bonus numbers for each prize</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            <div className="space-y-2">
              <Label className="text-amber-600 font-bold">1st Prize</Label>
              <Input
                placeholder="00"
                maxLength={2}
                value={results.first}
                onChange={(e) => setResults({ ...results, first: e.target.value })}
                className="text-center text-xl font-bold"
                data-testid="result-first"
              />
              <Input
                placeholder="Bonus (5 digits)"
                maxLength={5}
                value={results.first_bonus}
                onChange={(e) => setResults({ ...results, first_bonus: e.target.value })}
                className="text-center text-sm bg-amber-50 border-amber-200"
                data-testid="result-first-bonus"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-500 font-bold">2nd Prize</Label>
              <Input
                placeholder="00"
                maxLength={2}
                value={results.second}
                onChange={(e) => setResults({ ...results, second: e.target.value })}
                className="text-center text-xl font-bold"
                data-testid="result-second"
              />
              <Input
                placeholder="Bonus (5 digits)"
                maxLength={5}
                value={results.second_bonus}
                onChange={(e) => setResults({ ...results, second_bonus: e.target.value })}
                className="text-center text-sm bg-slate-50 border-slate-200"
                data-testid="result-second-bonus"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-orange-700 font-bold">3rd Prize</Label>
              <Input
                placeholder="00"
                maxLength={2}
                value={results.third}
                onChange={(e) => setResults({ ...results, third: e.target.value })}
                className="text-center text-xl font-bold"
                data-testid="result-third"
              />
              <Input
                placeholder="Bonus (5 digits)"
                maxLength={5}
                value={results.third_bonus}
                onChange={(e) => setResults({ ...results, third_bonus: e.target.value })}
                className="text-center text-sm bg-orange-50 border-orange-200"
                data-testid="result-third-bonus"
              />
            </div>
          </div>
          <div className="mb-6">
            <Label className="text-slate-600">Consolation Prizes (10) - Number & Bonus</Label>
            <div className="grid grid-cols-5 gap-3 mt-2">
              {results.consolations.map((c, i) => (
                <div key={i} className="space-y-1">
                  <Input
                    placeholder="00"
                    maxLength={2}
                    value={c}
                    onChange={(e) => updateConsolation(i, e.target.value)}
                    className="text-center text-sm"
                    data-testid={`consolation-${i}`}
                  />
                  <Input
                    placeholder="Bonus"
                    maxLength={5}
                    value={results.consolation_bonuses[i] || ""}
                    onChange={(e) => updateConsolationBonus(i, e.target.value)}
                    className="text-center text-xs bg-purple-50 border-purple-200"
                    data-testid={`consolation-bonus-${i}`}
                  />
                </div>
              ))}
            </div>
          </div>
          <Button onClick={handleSetResults} className="w-full bg-pink-600 hover:bg-pink-700" data-testid="set-results-btn">
            Set Results
          </Button>
          
          {currentResults && (
            <div className="mt-4 p-4 bg-slate-50 rounded-xl">
              <p className="text-sm font-medium mb-2">Current Results:</p>
              <div className="space-y-1 text-sm">
                <div><strong className="text-amber-600">1st:</strong> {currentResults.first} {currentResults.first_bonus && <span className="text-amber-500 ml-2">Bonus: {currentResults.first_bonus}</span>}</div>
                <div><strong className="text-slate-500">2nd:</strong> {currentResults.second} {currentResults.second_bonus && <span className="text-slate-400 ml-2">Bonus: {currentResults.second_bonus}</span>}</div>
                <div><strong className="text-orange-700">3rd:</strong> {currentResults.third} {currentResults.third_bonus && <span className="text-orange-500 ml-2">Bonus: {currentResults.third_bonus}</span>}</div>
              </div>
              {currentResults.consolations?.length > 0 && (
                <div className="text-xs mt-2 text-slate-500">
                  <p>Consolations: {currentResults.consolations.map((num, i) => {
                    const bonus = currentResults.consolation_bonuses?.[i];
                    return bonus ? `${num}(${bonus})` : num;
                  }).join(", ")}</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Entries List */}
      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle>Sales List (Sorted by Amount: Low to High)</CardTitle>
          <CardDescription>All entries for current draw</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p>Loading...</p>
          ) : entries.length === 0 ? (
            <p className="text-slate-500 text-center py-8">No entries yet for this draw.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2">Name</th>
                    <th className="text-left p-2">Phone</th>
                    <th className="text-center p-2">Number</th>
                    <th className="text-right p-2">Amount</th>
                    <th className="text-left p-2">Agent</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry, i) => (
                    <tr key={i} className="border-b hover:bg-slate-50">
                      <td className="p-2">{entry.name}</td>
                      <td className="p-2">{entry.phone}</td>
                      <td className="p-2 text-center font-mono font-bold">{entry.number}</td>
                      <td className="p-2 text-right font-medium">₹{entry.amount}</td>
                      <td className="p-2 text-slate-500">{entry.agent}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ============ ADMIN PAGE ============
const Admin = () => {
  const [activeTab, setActiveTab] = useState("admins");
  const [competitions, setCompetitions] = useState([]);
  const [coAdmins, setCoAdmins] = useState([]);
  const [students, setStudents] = useState([]);
  const [districtSettings, setDistrictSettings] = useState([]);
  const [allMessages, setAllMessages] = useState([]);
  const [courses, setCourses] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [activityPosts, setActivityPosts] = useState([]);
  const [messageDistrictFilter, setMessageDistrictFilter] = useState("all");
  const [messageCoAdminFilter, setMessageCoAdminFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [newCoAdmin, setNewCoAdmin] = useState({ name: "", username: "", password: "", email: "", district: "" });
  const [newAdmin, setNewAdmin] = useState({ name: "", phone: "", password: "", email: "" });
  const [newCourse, setNewCourse] = useState({ title: "", description: "", district: "" });
  const [newActivityPost, setNewActivityPost] = useState({ title: "", content: "", category: "general", media_data: null, media_type: null });
  const [selectedDistrict, setSelectedDistrict] = useState("all");
  const [editingCoAdmin, setEditingCoAdmin] = useState(null);
  const [editCoAdminData, setEditCoAdminData] = useState({ name: "", username: "", email: "", district: "", password: "" });
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [editAdminData, setEditAdminData] = useState({ name: "", phone: "", email: "", password: "" });
  const [paymentEnabled, setPaymentEnabled] = useState(false);
  // Member Data states
  const [dropoutEnrollments, setDropoutEnrollments] = useState([]);
  const [educatedUnemployed, setEducatedUnemployed] = useState([]);
  const [memberDataTab, setMemberDataTab] = useState("dropouts");
  const [memberDataDistrict, setMemberDataDistrict] = useState("all");
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [editingStudent, setEditingStudent] = useState(null);
  const [editStudentData, setEditStudentData] = useState({ name: "", phone: "", email: "", district: "", user_type: "" });
  const [editingMessage, setEditingMessage] = useState(null);
  const [editMessageContent, setEditMessageContent] = useState("");

  const districts = [
    "Headquarter",
    "West Garo Hills",
    "East Garo Hills",
    "North Garo Hills",
    "South Garo Hills",
    "South West Garo Hills"
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem("token");
    try {
      const [compRes, coAdminRes, studentsRes, settingsRes, messagesRes, coursesRes, adminsRes, postsRes, paymentRes, enrollmentsRes, educatedRes] = await Promise.all([
        axios.get(`${API}/competitions?include_inactive=true`),
        axios.get(`${API}/admin/co-admins`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/students`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/district-settings`),
        axios.get(`${API}/admin/messages`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/courses?include_inactive=true`),
        axios.get(`${API}/admin/admins`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/activity-posts`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/settings/payment`),
        axios.get(`${API}/admin/enrollments`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/educated-unemployed`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setCompetitions(compRes.data);
      setCoAdmins(coAdminRes.data.co_admins || []);
      setStudents(studentsRes.data.students || []);
      setDistrictSettings(settingsRes.data.settings || []);
      setAllMessages(messagesRes.data.messages || []);
      setCourses(coursesRes.data.courses || []);
      setAdmins(adminsRes.data.admins || []);
      setActivityPosts(postsRes.data.posts || []);
      setPaymentEnabled(paymentRes.data.enabled || false);
      setDropoutEnrollments(enrollmentsRes.data.students || []);
      setEducatedUnemployed(educatedRes.data.users || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const togglePayment = async () => {
    const token = localStorage.getItem("token");
    try {
      const res = await axios.patch(`${API}/admin/settings/payment`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPaymentEnabled(res.data.enabled);
      alert(`Payment ${res.data.enabled ? 'enabled' : 'disabled'} successfully!`);
    } catch (error) {
      alert(error.response?.data?.detail || "Error updating payment settings");
    }
  };

  const createCoAdmin = async (e) => {
    e.preventDefault();
    if (!newCoAdmin.name || !newCoAdmin.username || !newCoAdmin.password || !newCoAdmin.email || !newCoAdmin.district) {
      alert("Please fill in all fields");
      return;
    }
    try {
      await axios.post(`${API}/admin/co-admins`, newCoAdmin);
      setNewCoAdmin({ name: "", username: "", password: "", email: "", district: "" });
      fetchData();
      alert("Co-Admin created successfully!");
    } catch (error) {
      alert(error.response?.data?.detail || "Error creating co-admin");
    }
  };

  const deleteCoAdmin = async (id) => {
    if (!window.confirm("Are you sure you want to delete this co-admin?")) return;
    const token = localStorage.getItem("token");
    try {
      await axios.delete(`${API}/admin/co-admins/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || "Error deleting co-admin");
    }
  };

  const openEditCoAdmin = (coAdmin) => {
    setEditingCoAdmin(coAdmin);
    setEditCoAdminData({
      name: coAdmin.name || "",
      username: coAdmin.username || "",
      email: coAdmin.email || "",
      district: coAdmin.district || "",
      password: ""  // Leave blank, only update if user enters new password
    });
  };

  const saveCoAdminChanges = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");
    
    // Build update data, only include password if provided
    const updateData = {
      name: editCoAdminData.name,
      username: editCoAdminData.username,
      email: editCoAdminData.email,
      district: editCoAdminData.district
    };
    
    if (editCoAdminData.password.trim()) {
      updateData.password = editCoAdminData.password;
    }
    
    try {
      await axios.patch(`${API}/admin/co-admins/${editingCoAdmin.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingCoAdmin(null);
      fetchData();
      alert("Co-admin updated successfully!");
    } catch (error) {
      alert(error.response?.data?.detail || "Error updating co-admin");
    }
  };

  const createAdmin = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");
    if (!newAdmin.name || !newAdmin.phone || !newAdmin.password) {
      alert("Please fill in name, phone, and password");
      return;
    }
    try {
      await axios.post(`${API}/admin/admins`, newAdmin, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewAdmin({ name: "", phone: "", password: "", email: "" });
      fetchData();
      alert("Admin created successfully!");
    } catch (error) {
      alert(error.response?.data?.detail || "Error creating admin");
    }
  };

  const deleteAdmin = async (id) => {
    if (!window.confirm("Are you sure you want to delete this admin?")) return;
    const token = localStorage.getItem("token");
    try {
      await axios.delete(`${API}/admin/admins/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || "Error deleting admin");
    }
  };

  const openEditAdmin = (admin) => {
    setEditingAdmin(admin);
    setEditAdminData({
      name: admin.name || "",
      phone: admin.phone || "",
      email: admin.email || "",
      password: ""
    });
  };

  const saveAdminChanges = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");
    
    const updateData = {
      name: editAdminData.name,
      phone: editAdminData.phone,
      email: editAdminData.email
    };
    
    if (editAdminData.password.trim()) {
      updateData.password = editAdminData.password;
    }
    
    try {
      await axios.patch(`${API}/admin/admins/${editingAdmin.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingAdmin(null);
      fetchData();
      alert("Admin updated successfully!");
    } catch (error) {
      alert(error.response?.data?.detail || "Error updating admin");
    }
  };

  const toggleDistrict = async (district) => {
    try {
      await axios.patch(`${API}/admin/districts/${encodeURIComponent(district)}/toggle`);
      fetchData();
    } catch (error) {
      alert("Error toggling district");
    }
  };

  const isDistrictActive = (district) => {
    const setting = districtSettings.find(s => s.district === district);
    return setting ? setting.is_active : true;
  };

  const getStudentsByDistrict = (district) => {
    if (district === "all") return students;
    return students.filter(s => s.district === district);
  };

  const toggleCompetition = async (id, isActive) => {
    try {
      await axios.patch(`${API}/competitions/${id}/${isActive ? 'deactivate' : 'activate'}`);
      fetchData();
    } catch (error) {
      console.error("Error toggling competition:", error);
    }
  };

  const seedData = async () => {
    try {
      await axios.post(`${API}/seed-data`);
      fetchData();
      alert("Data seeded successfully!");
    } catch (error) {
      console.error("Error seeding data:", error);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewActivity({ ...newActivity, image_data: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const createActivityPost = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");
    if (!newActivityPost.title || !newActivityPost.content) {
      alert("Please fill in title and content");
      return;
    }
    try {
      await axios.post(`${API}/activity-posts`, newActivityPost, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewActivityPost({ title: "", content: "", category: "general", media_data: null, media_type: null });
      fetchData();
      alert("Activity post created successfully!");
    } catch (error) {
      alert(error.response?.data?.detail || "Error creating activity post");
    }
  };

  const deleteActivityPost = async (id) => {
    if (!window.confirm("Are you sure you want to delete this post?")) return;
    const token = localStorage.getItem("token");
    try {
      await axios.delete(`${API}/activity-posts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || "Error deleting activity post");
    }
  };

  const handleActivityMediaChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const isVideo = file.type.startsWith("video/");
        setNewActivityPost({
          ...newActivityPost,
          media_data: event.target.result,
          media_type: isVideo ? "video" : "image"
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const createCourse = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");
    if (!newCourse.title || !newCourse.description) {
      alert("Please enter course title and description");
      return;
    }
    try {
      await axios.post(`${API}/courses`, newCourse, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewCourse({ title: "", description: "", district: "" });
      fetchData();
      alert("Course created successfully!");
    } catch (error) {
      alert(error.response?.data?.detail || "Error creating course");
    }
  };

  const deleteCourse = async (courseId) => {
    if (!window.confirm("Are you sure you want to delete this course? All lessons and assignments will also be deleted.")) return;
    const token = localStorage.getItem("token");
    try {
      await axios.delete(`${API}/courses/${courseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || "Error deleting course");
    }
  };

  const toggleCourseActive = async (courseId, currentStatus) => {
    const token = localStorage.getItem("token");
    try {
      await axios.patch(`${API}/courses/${courseId}`, { is_active: !currentStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || "Error updating course");
    }
  };

  const updateCompetitionSettings = async (competitionId, settings) => {
    const token = localStorage.getItem("token");
    try {
      await axios.patch(`${API}/competitions/${competitionId}/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || "Error updating competition settings");
    }
  };

  // Student edit/delete functions
  const openEditStudent = (student) => {
    setEditingStudent(student);
    setEditStudentData({
      name: student.name || "",
      phone: student.phone || "",
      email: student.email || "",
      district: student.district || "",
      user_type: student.user_type || "student"
    });
  };

  const saveStudentChanges = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");
    try {
      await axios.patch(`${API}/admin/members/${editingStudent.id}`, editStudentData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingStudent(null);
      fetchData();
      alert("Student updated successfully!");
    } catch (error) {
      alert(error.response?.data?.detail || "Error updating student");
    }
  };

  const deleteStudent = async (studentId, studentName) => {
    if (!window.confirm(`Are you sure you want to delete "${studentName}"? This will also delete all their enrollment documents and messages. This action cannot be undone.`)) return;
    const token = localStorage.getItem("token");
    try {
      await axios.delete(`${API}/admin/members/${studentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
      alert("Student deleted successfully!");
    } catch (error) {
      alert(error.response?.data?.detail || "Error deleting student");
    }
  };

  // Enrollment document delete function
  const deleteEnrollmentDocs = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete all enrollment documents for "${userName}"? This action cannot be undone.`)) return;
    const token = localStorage.getItem("token");
    try {
      await axios.delete(`${API}/admin/enrollments/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedEnrollment(null);
      fetchData();
      alert("Enrollment documents deleted successfully!");
    } catch (error) {
      alert(error.response?.data?.detail || "Error deleting enrollment documents");
    }
  };

  // Message edit/delete functions
  const openEditMessage = (message) => {
    setEditingMessage(message);
    setEditMessageContent(message.content || "");
  };

  const saveMessageChanges = async () => {
    if (!editMessageContent.trim()) {
      alert("Message content cannot be empty");
      return;
    }
    const token = localStorage.getItem("token");
    try {
      await axios.patch(`${API}/admin/messages/${editingMessage.id}`, { content: editMessageContent }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingMessage(null);
      setEditMessageContent("");
      fetchData();
      alert("Message updated successfully!");
    } catch (error) {
      alert(error.response?.data?.detail || "Error updating message");
    }
  };

  const deleteMessage = async (messageId) => {
    if (!window.confirm("Are you sure you want to delete this message?")) return;
    const token = localStorage.getItem("token");
    try {
      await axios.delete(`${API}/messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
      alert("Message deleted successfully!");
    } catch (error) {
      alert(error.response?.data?.detail || "Error deleting message");
    }
  };

  return (
    <div className="min-h-screen bg-transparent" data-testid="admin-page">
      <Navbar />
      <div className="pt-24 pb-16 max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800">Admin Panel</h1>
          <Button onClick={seedData} variant="outline" size="sm" data-testid="seed-data-btn">
            Seed Data
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="flex flex-wrap gap-1">
            <TabsTrigger value="admins">Admins</TabsTrigger>
            <TabsTrigger value="co-admins">Co-Admins</TabsTrigger>
            <TabsTrigger value="member-data">Member Data</TabsTrigger>
            <TabsTrigger value="students">Students</TabsTrigger>
            <TabsTrigger value="districts">Districts</TabsTrigger>
            <TabsTrigger value="messages">Messages</TabsTrigger>
            <TabsTrigger value="courses">Courses</TabsTrigger>
            <TabsTrigger value="competitions">Competitions</TabsTrigger>
            <TabsTrigger value="activity-posts">Activities Corner</TabsTrigger>
            <TabsTrigger value="duna100">Duna 100</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
          </TabsList>

          {/* Admins Tab */}
          <TabsContent value="admins">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Create Admin Form */}
              <Card className="rounded-2xl">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="w-5 h-5 text-purple-600" />
                    Add New Admin
                  </CardTitle>
                  <CardDescription>Create another admin to manage activities and news</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={createAdmin} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Full Name</Label>
                      <Input
                        placeholder="Admin name"
                        value={newAdmin.name}
                        onChange={(e) => setNewAdmin({...newAdmin, name: e.target.value})}
                        className="rounded-xl"
                        data-testid="admin-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Phone (Login ID)</Label>
                      <Input
                        placeholder="Phone number for login"
                        value={newAdmin.phone}
                        onChange={(e) => setNewAdmin({...newAdmin, phone: e.target.value})}
                        className="rounded-xl"
                        data-testid="admin-phone-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email (Optional)</Label>
                      <Input
                        type="email"
                        placeholder="Email address"
                        value={newAdmin.email}
                        onChange={(e) => setNewAdmin({...newAdmin, email: e.target.value})}
                        className="rounded-xl"
                        data-testid="admin-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Password</Label>
                      <Input
                        type="password"
                        placeholder="Set password"
                        value={newAdmin.password}
                        onChange={(e) => setNewAdmin({...newAdmin, password: e.target.value})}
                        className="rounded-xl"
                        data-testid="admin-password-input"
                      />
                    </div>
                    <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700 rounded-full" data-testid="create-admin-btn">
                      Create Admin
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Admins List */}
              <Card className="rounded-2xl">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-purple-600" />
                    Current Admins
                  </CardTitle>
                  <CardDescription>{admins.length} admin(s)</CardDescription>
                </CardHeader>
                <CardContent>
                  {admins.length === 0 ? (
                    <p className="text-slate-500 text-center py-4">No admins found</p>
                  ) : (
                    <div className="space-y-3">
                      {admins.map((admin) => (
                        <div key={admin.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl" data-testid={`admin-${admin.id}`}>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                              <User className="w-5 h-5 text-purple-600" />
                            </div>
                            <div>
                              <p className="font-medium">{admin.name}</p>
                              <p className="text-xs text-slate-500">Phone: {admin.phone}</p>
                              {admin.email && <p className="text-xs text-slate-400">{admin.email}</p>}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openEditAdmin(admin)}
                              className="text-blue-600 hover:bg-blue-50"
                              data-testid={`edit-admin-${admin.id}-btn`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => deleteAdmin(admin.id)}
                              className="text-red-600 hover:bg-red-50"
                              data-testid={`delete-admin-${admin.id}-btn`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Co-Admins Tab */}
          <TabsContent value="co-admins">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Create Co-Admin Form */}
              <Card className="rounded-2xl">
                <CardHeader>
                  <CardTitle>Create Co-Admin</CardTitle>
                  <CardDescription>Add a co-admin for a district</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={createCoAdmin} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Full Name</Label>
                      <Input
                        placeholder="Co-admin's full name"
                        value={newCoAdmin.name}
                        onChange={(e) => setNewCoAdmin({...newCoAdmin, name: e.target.value})}
                        className="rounded-xl"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Username (for login)</Label>
                      <Input
                        placeholder="Unique username"
                        value={newCoAdmin.username}
                        onChange={(e) => setNewCoAdmin({...newCoAdmin, username: e.target.value})}
                        className="rounded-xl"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input
                        type="email"
                        placeholder="email@example.com"
                        value={newCoAdmin.email}
                        onChange={(e) => setNewCoAdmin({...newCoAdmin, email: e.target.value})}
                        className="rounded-xl"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Password</Label>
                      <Input
                        type="password"
                        placeholder="Password"
                        value={newCoAdmin.password}
                        onChange={(e) => setNewCoAdmin({...newCoAdmin, password: e.target.value})}
                        className="rounded-xl"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Assign to District</Label>
                      <select
                        value={newCoAdmin.district}
                        onChange={(e) => setNewCoAdmin({...newCoAdmin, district: e.target.value})}
                        className="w-full p-3 border rounded-xl bg-white"
                      >
                        <option value="">Select district</option>
                        {districts.map((d) => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </select>
                    </div>
                    <Button type="submit" className="w-full bg-teal-600 hover:bg-teal-700 rounded-full">
                      Create Co-Admin
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Co-Admins List */}
              <Card className="rounded-2xl">
                <CardHeader>
                  <CardTitle>Current Co-Admins</CardTitle>
                  <CardDescription>{coAdmins.length} co-admin(s) assigned</CardDescription>
                </CardHeader>
                <CardContent>
                  {coAdmins.length === 0 ? (
                    <p className="text-slate-500 text-center py-8">No co-admins created yet</p>
                  ) : (
                    <div className="space-y-3">
                      {coAdmins.map((ca) => (
                        <div key={ca.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                          <div>
                            <p className="font-medium">{ca.name}</p>
                            <p className="text-sm text-slate-500">@{ca.username} • {ca.district}</p>
                            {ca.email && <p className="text-xs text-slate-400">{ca.email}</p>}
                          </div>
                          <div className="flex items-center gap-2">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                              onClick={() => openEditCoAdmin(ca)}
                              data-testid={`edit-coadmin-${ca.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => deleteCoAdmin(ca.id)}
                              data-testid={`delete-coadmin-${ca.id}`}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Edit Co-Admin Modal */}
              {editingCoAdmin && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                  <Card className="w-full max-w-md rounded-2xl">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Edit className="w-5 h-5 text-teal-600" />
                        Edit Co-Admin
                      </CardTitle>
                      <CardDescription>Update co-admin details. Leave password blank to keep current.</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <form onSubmit={saveCoAdminChanges} className="space-y-4">
                        <div className="space-y-2">
                          <Label>Full Name</Label>
                          <Input
                            placeholder="Co-admin's full name"
                            value={editCoAdminData.name}
                            onChange={(e) => setEditCoAdminData({...editCoAdminData, name: e.target.value})}
                            className="rounded-xl"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Username (for login)</Label>
                          <Input
                            placeholder="Unique username"
                            value={editCoAdminData.username}
                            onChange={(e) => setEditCoAdminData({...editCoAdminData, username: e.target.value})}
                            className="rounded-xl"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Email</Label>
                          <Input
                            type="email"
                            placeholder="email@example.com"
                            value={editCoAdminData.email}
                            onChange={(e) => setEditCoAdminData({...editCoAdminData, email: e.target.value})}
                            className="rounded-xl"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>District</Label>
                          <select
                            value={editCoAdminData.district}
                            onChange={(e) => setEditCoAdminData({...editCoAdminData, district: e.target.value})}
                            className="w-full p-3 border rounded-xl bg-white"
                            required
                          >
                            <option value="">Select district</option>
                            {districts.map((d) => (
                              <option key={d} value={d}>{d}</option>
                            ))}
                          </select>
                        </div>
                        <div className="space-y-2">
                          <Label>New Password (leave blank to keep current)</Label>
                          <Input
                            type="password"
                            placeholder="Enter new password"
                            value={editCoAdminData.password}
                            onChange={(e) => setEditCoAdminData({...editCoAdminData, password: e.target.value})}
                            className="rounded-xl"
                          />
                        </div>
                        <div className="flex gap-3 pt-2">
                          <Button
                            type="button"
                            variant="outline"
                            className="flex-1 rounded-full"
                            onClick={() => setEditingCoAdmin(null)}
                          >
                            Cancel
                          </Button>
                          <Button type="submit" className="flex-1 bg-teal-600 hover:bg-teal-700 rounded-full">
                            Save Changes
                          </Button>
                        </div>
                      </form>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Edit Admin Modal */}
          {editingAdmin && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <Card className="w-full max-w-md rounded-2xl">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Edit className="w-5 h-5 text-purple-600" />
                    Edit Admin
                  </CardTitle>
                  <CardDescription>Update admin details. Leave password blank to keep current.</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={saveAdminChanges} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Full Name</Label>
                      <Input
                        placeholder="Admin's full name"
                        value={editAdminData.name}
                        onChange={(e) => setEditAdminData({...editAdminData, name: e.target.value})}
                        className="rounded-xl"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Phone</Label>
                      <Input
                        placeholder="Phone number"
                        value={editAdminData.phone}
                        onChange={(e) => setEditAdminData({...editAdminData, phone: e.target.value})}
                        className="rounded-xl"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input
                        type="email"
                        placeholder="email@example.com"
                        value={editAdminData.email}
                        onChange={(e) => setEditAdminData({...editAdminData, email: e.target.value})}
                        className="rounded-xl"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>New Password (leave blank to keep current)</Label>
                      <Input
                        type="password"
                        placeholder="Enter new password"
                        value={editAdminData.password}
                        onChange={(e) => setEditAdminData({...editAdminData, password: e.target.value})}
                        className="rounded-xl"
                      />
                    </div>
                    <div className="flex gap-3 pt-2">
                      <Button
                        type="button"
                        variant="outline"
                        className="flex-1 rounded-full"
                        onClick={() => setEditingAdmin(null)}
                      >
                        Cancel
                      </Button>
                      <Button type="submit" className="flex-1 bg-purple-600 hover:bg-purple-700 rounded-full">
                        Save Changes
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Member Data Tab */}
          <TabsContent value="member-data">
            <Card className="rounded-2xl">
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-teal-600" />
                      Member Data & Documents
                    </CardTitle>
                    <CardDescription>View enrollment documents and career dreams submitted by members</CardDescription>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <select
                      value={memberDataTab}
                      onChange={(e) => setMemberDataTab(e.target.value)}
                      className="p-2 border rounded-xl bg-white text-sm"
                    >
                      <option value="dropouts">Dropout Enrollments</option>
                      <option value="educated">Educated Unemployed Dreams</option>
                    </select>
                    <select
                      value={memberDataDistrict}
                      onChange={(e) => setMemberDataDistrict(e.target.value)}
                      className="p-2 border rounded-xl bg-white text-sm"
                    >
                      <option value="all">All Districts</option>
                      {districts.map((d) => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {memberDataTab === "dropouts" ? (
                  // Dropout Enrollments View
                  <div>
                    <div className="mb-4 p-3 bg-teal-50 rounded-xl">
                      <p className="text-sm text-teal-700">
                        <strong>Dropout Enrollment Documents:</strong> View Aadhar, Birth Certificate, Registration Card, Photo, and WhatsApp details submitted by dropout students.
                      </p>
                    </div>
                    {dropoutEnrollments.filter(s => memberDataDistrict === "all" || s.district === memberDataDistrict).length === 0 ? (
                      <div className="text-center py-8">
                        <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                        <p className="text-slate-500">No dropout enrollments found</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {dropoutEnrollments
                          .filter(s => memberDataDistrict === "all" || s.district === memberDataDistrict)
                          .map((student) => (
                            <div key={student.id} className="p-4 border rounded-xl bg-white hover:shadow-md transition-shadow" data-testid={`enrollment-${student.id}`}>
                              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
                                <div>
                                  <h4 className="font-semibold text-slate-800">{student.name}</h4>
                                  <p className="text-sm text-slate-500">{student.phone} • {student.email}</p>
                                  <Badge className="mt-1 bg-purple-100 text-purple-700 text-xs">{student.district}</Badge>
                                </div>
                                <div className="flex items-center gap-2">
                                  {student.enrollment ? (
                                    <Badge className="bg-green-100 text-green-700">Documents Submitted</Badge>
                                  ) : (
                                    <Badge className="bg-amber-100 text-amber-700">Pending Documents</Badge>
                                  )}
                                  {student.enrollment && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => setSelectedEnrollment(selectedEnrollment?.user_id === student.id ? null : {...student.enrollment, user_id: student.id, user_name: student.name})}
                                      className="rounded-full"
                                    >
                                      <Eye className="w-4 h-4 mr-1" />
                                      {selectedEnrollment?.user_id === student.id ? 'Hide' : 'View'}
                                    </Button>
                                  )}
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => openEditStudent(student)}
                                    className="rounded-full text-blue-600 hover:bg-blue-50"
                                    data-testid={`edit-enrollment-student-${student.id}`}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => deleteStudent(student.id, student.name)}
                                    className="rounded-full text-red-600 hover:bg-red-50"
                                    data-testid={`delete-enrollment-student-${student.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                              {/* Expanded Document View */}
                              {selectedEnrollment?.user_id === student.id && student.enrollment && (
                                <div className="mt-4 p-4 bg-slate-50 rounded-xl space-y-4">
                                  <div className="flex items-center justify-between border-b pb-2">
                                    <h5 className="font-medium text-slate-700">Enrollment Documents for {student.name}</h5>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => deleteEnrollmentDocs(student.id, student.name)}
                                      className="rounded-full text-red-600 hover:bg-red-50"
                                      data-testid={`delete-docs-${student.id}`}
                                    >
                                      <Trash2 className="w-4 h-4 mr-1" />
                                      Delete Documents
                                    </Button>
                                  </div>
                                  
                                  {student.enrollment.whatsapp_number && (
                                    <div className="flex items-center gap-2">
                                      <Phone className="w-4 h-4 text-green-600" />
                                      <span className="text-sm"><strong>WhatsApp:</strong> {student.enrollment.whatsapp_number}</span>
                                    </div>
                                  )}
                                  
                                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {student.enrollment.aadhar_card && (
                                      <div className="border rounded-lg p-2 bg-white">
                                        <p className="text-xs text-slate-500 mb-2">Aadhar Card</p>
                                        <img src={student.enrollment.aadhar_card} alt="Aadhar Card" className="w-full h-32 object-contain rounded" />
                                      </div>
                                    )}
                                    {student.enrollment.birth_certificate && (
                                      <div className="border rounded-lg p-2 bg-white">
                                        <p className="text-xs text-slate-500 mb-2">Birth Certificate</p>
                                        <img src={student.enrollment.birth_certificate} alt="Birth Certificate" className="w-full h-32 object-contain rounded" />
                                      </div>
                                    )}
                                    {student.enrollment.registration_card && (
                                      <div className="border rounded-lg p-2 bg-white">
                                        <p className="text-xs text-slate-500 mb-2">Registration Card</p>
                                        <img src={student.enrollment.registration_card} alt="Registration Card" className="w-full h-32 object-contain rounded" />
                                      </div>
                                    )}
                                    {student.enrollment.passport_photo && (
                                      <div className="border rounded-lg p-2 bg-white">
                                        <p className="text-xs text-slate-500 mb-2">Passport Photo</p>
                                        <img src={student.enrollment.passport_photo} alt="Passport Photo" className="w-full h-32 object-contain rounded" />
                                      </div>
                                    )}
                                    {student.enrollment.self_signature && (
                                      <div className="border rounded-lg p-2 bg-white">
                                        <p className="text-xs text-slate-500 mb-2">Signature</p>
                                        <img src={student.enrollment.self_signature} alt="Signature" className="w-full h-32 object-contain rounded" />
                                      </div>
                                    )}
                                  </div>
                                  
                                  {!student.enrollment.aadhar_card && !student.enrollment.birth_certificate && !student.enrollment.registration_card && !student.enrollment.passport_photo && !student.enrollment.self_signature && (
                                    <p className="text-sm text-slate-500 italic">No document images uploaded yet.</p>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                ) : (
                  // Educated Unemployed Dreams View
                  <div>
                    <div className="mb-4 p-3 bg-terracotta/10 rounded-xl">
                      <p className="text-sm text-terracotta">
                        <strong>Educated Unemployed Career Dreams:</strong> View the career aspirations (Employment or Self-Employment) selected by educated unemployed members.
                      </p>
                    </div>
                    {educatedUnemployed.filter(u => memberDataDistrict === "all" || u.district === memberDataDistrict).length === 0 ? (
                      <div className="text-center py-8">
                        <TrendingUp className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                        <p className="text-slate-500">No educated unemployed members found</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b bg-slate-50">
                              <th className="text-left py-3 px-3">Name</th>
                              <th className="text-left py-3 px-3">Phone</th>
                              <th className="text-left py-3 px-3">District</th>
                              <th className="text-left py-3 px-3">Dream Option</th>
                              <th className="text-left py-3 px-3">Registered</th>
                            </tr>
                          </thead>
                          <tbody>
                            {educatedUnemployed
                              .filter(u => memberDataDistrict === "all" || u.district === memberDataDistrict)
                              .map((user) => (
                                <tr key={user.id} className="border-b hover:bg-slate-50" data-testid={`educated-${user.id}`}>
                                  <td className="py-3 px-3">
                                    <div>
                                      <p className="font-medium">{user.name}</p>
                                      <p className="text-xs text-slate-500">{user.email}</p>
                                    </div>
                                  </td>
                                  <td className="py-3 px-3">{user.phone}</td>
                                  <td className="py-3 px-3">
                                    <Badge className="bg-teal-100 text-teal-700 text-xs">{user.district}</Badge>
                                  </td>
                                  <td className="py-3 px-3">
                                    {user.dream_submitted ? (
                                      <Badge className={`text-xs ${user.dream_option === 'employed' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                                        {user.dream_option === 'employed' ? 'Employment' : 'Self-Employment'}
                                      </Badge>
                                    ) : (
                                      <Badge className="bg-amber-100 text-amber-700 text-xs">Not Selected</Badge>
                                    )}
                                  </td>
                                  <td className="py-3 px-3 text-slate-500 text-xs">
                                    {new Date(user.created_at).toLocaleDateString()}
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Students Tab */}
          <TabsContent value="students">
            <Card className="rounded-2xl">
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <CardTitle>Registered Students</CardTitle>
                    <CardDescription>{getStudentsByDistrict(selectedDistrict).length} student(s)</CardDescription>
                  </div>
                  <select
                    value={selectedDistrict}
                    onChange={(e) => setSelectedDistrict(e.target.value)}
                    className="p-2 border rounded-xl bg-white text-sm"
                  >
                    <option value="all">All Districts</option>
                    {districts.map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>
              </CardHeader>
              <CardContent>
                {getStudentsByDistrict(selectedDistrict).length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No students registered yet</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-2">Name</th>
                          <th className="text-left py-3 px-2">Phone</th>
                          <th className="text-left py-3 px-2">Type</th>
                          <th className="text-left py-3 px-2">District</th>
                          <th className="text-left py-3 px-2">Registered</th>
                          <th className="text-left py-3 px-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getStudentsByDistrict(selectedDistrict).map((student) => (
                          <tr key={student.id} className="border-b hover:bg-slate-50">
                            <td className="py-3 px-2">
                              <div>
                                <p className="font-medium">{student.name}</p>
                                <p className="text-xs text-slate-500">{student.email}</p>
                              </div>
                            </td>
                            <td className="py-3 px-2">{student.phone}</td>
                            <td className="py-3 px-2">
                              <Badge className={`text-xs ${
                                student.user_type === 'woman' ? 'bg-pink-100 text-pink-700' :
                                student.user_type === 'girl' ? 'bg-purple-100 text-purple-700' :
                                'bg-blue-100 text-blue-700'
                              }`}>
                                {student.user_type === 'woman' ? 'Educated Unemployed' : 
                                 student.user_type === 'girl' ? 'Dropout' : 
                                 student.user_type === 'general' ? 'General' : 'Member'}
                              </Badge>
                            </td>
                            <td className="py-3 px-2">
                              <Badge className="bg-teal-100 text-teal-700 text-xs">{student.district}</Badge>
                            </td>
                            <td className="py-3 px-2 text-slate-500 text-xs">
                              {new Date(student.created_at).toLocaleDateString()}
                            </td>
                            <td className="py-3 px-2">
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => openEditStudent(student)}
                                  className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-lg"
                                  title="Edit student"
                                  data-testid={`edit-student-${student.id}`}
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => deleteStudent(student.id, student.name)}
                                  className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"
                                  title="Delete student"
                                  data-testid={`delete-student-${student.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Districts Tab */}
          <TabsContent value="districts">
            <Card className="rounded-2xl">
              <CardHeader>
                <CardTitle>District Management</CardTitle>
                <CardDescription>Enable or disable district access</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {districts.map((district) => {
                    const coAdmin = coAdmins.find(ca => ca.district === district);
                    const studentCount = students.filter(s => s.district === district).length;
                    const isActive = isDistrictActive(district);
                    
                    return (
                      <div key={district} className={`p-4 rounded-xl border ${isActive ? 'bg-white' : 'bg-slate-100'}`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-medium">{district}</h3>
                            <p className="text-sm text-slate-500">
                              {coAdmin ? `Co-Admin: ${coAdmin.name}` : 'No co-admin assigned'} • {studentCount} students
                            </p>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge className={isActive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}>
                              {isActive ? "Active" : "Disabled"}
                            </Badge>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => toggleDistrict(district)}
                            >
                              {isActive ? "Disable" : "Enable"}
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Courses Tab */}
          <TabsContent value="courses">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Create Course Form */}
              <Card className="rounded-2xl">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Plus className="w-5 h-5 text-teal-600" />
                    Create Course
                  </CardTitle>
                  <CardDescription>Add a new course for students</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={createCourse} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Course Title</Label>
                      <Input
                        placeholder="e.g., Introduction to Mathematics"
                        value={newCourse.title}
                        onChange={(e) => setNewCourse({...newCourse, title: e.target.value})}
                        className="rounded-xl"
                        data-testid="course-title-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Textarea
                        placeholder="Describe what students will learn..."
                        value={newCourse.description}
                        onChange={(e) => setNewCourse({...newCourse, description: e.target.value})}
                        className="rounded-xl min-h-[100px]"
                        data-testid="course-description-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>District (Optional)</Label>
                      <select
                        value={newCourse.district}
                        onChange={(e) => setNewCourse({...newCourse, district: e.target.value})}
                        className="w-full rounded-xl border border-slate-200 px-3 py-2"
                        data-testid="course-district-select"
                      >
                        <option value="">All Districts</option>
                        {districts.map((d) => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </select>
                      <p className="text-xs text-slate-500">Leave empty to make available to all districts</p>
                    </div>
                    <Button type="submit" className="w-full bg-teal-600 hover:bg-teal-700 rounded-full" data-testid="create-course-btn">
                      Create Course
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Courses List */}
              <Card className="rounded-2xl">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-teal-600" />
                    Manage Courses
                  </CardTitle>
                  <CardDescription>{courses.length} course(s) created</CardDescription>
                </CardHeader>
                <CardContent>
                  {courses.length === 0 ? (
                    <p className="text-slate-500 text-center py-8">No courses created yet</p>
                  ) : (
                    <div className="space-y-3 max-h-[400px] overflow-y-auto">
                      {courses.map((course) => (
                        <div 
                          key={course.id} 
                          className={`p-4 rounded-xl border-2 ${course.is_active ? 'border-teal-200 bg-teal-50/50' : 'border-slate-200 bg-slate-50'}`}
                          data-testid={`admin-course-${course.id}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-semibold text-slate-800 truncate">{course.title}</h4>
                                <Badge className={course.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}>
                                  {course.is_active ? 'Active' : 'Inactive'}
                                </Badge>
                              </div>
                              <p className="text-sm text-slate-500 line-clamp-2">{course.description}</p>
                              <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                                <span>{course.lesson_count || 0} lessons</span>
                                <span>{course.assignment_count || 0} assignments</span>
                                {course.district && <Badge variant="outline" className="text-xs">{course.district}</Badge>}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Link to={`/courses/${course.id}/edit`}>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="rounded-full"
                                  data-testid={`edit-course-${course.id}-btn`}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                              </Link>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => toggleCourseActive(course.id, course.is_active)}
                                className="rounded-full"
                              >
                                {course.is_active ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => deleteCourse(course.id)}
                                className="rounded-full text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Course Content Management Info */}
            <Card className="mt-6 rounded-2xl bg-amber-50 border-amber-200">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <AlertCircle className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-amber-800 mb-1">Adding Course Content</h4>
                    <p className="text-sm text-amber-700">
                      To add lessons and assignments to a course, co-admins can use the course management features 
                      in their dashboard. Each co-admin can manage courses for their district.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="competitions">
            <Card className="rounded-2xl">
              <CardHeader>
                <CardTitle>Manage Competitions</CardTitle>
                <CardDescription>Activate/deactivate competitions and configure photo/video settings</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p>Loading...</p>
                ) : competitions.length === 0 ? (
                  <p className="text-slate-500">No competitions found. Click "Seed Data" to create them.</p>
                ) : (
                  <div className="space-y-4">
                    {competitions.map((comp) => (
                      <div key={comp.id} className="p-4 bg-slate-50 rounded-xl" data-testid={`admin-comp-${comp.id}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <p className="font-medium">{comp.name}</p>
                            <p className="text-sm text-slate-500">{comp.competition_type}</p>
                          </div>
                          <div className="flex items-center gap-4">
                            <Badge className={comp.is_active ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}>
                              {comp.is_active ? "Active" : "Inactive"}
                            </Badge>
                            <Button
                              size="sm"
                              variant={comp.is_active ? "destructive" : "default"}
                              onClick={() => toggleCompetition(comp.id, comp.is_active)}
                              data-testid={`toggle-${comp.id}`}
                            >
                              {comp.is_active ? "Deactivate" : "Activate"}
                            </Button>
                          </div>
                        </div>
                        <div className="flex items-center gap-6 pt-3 border-t">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={comp.allows_image || false}
                              onChange={(e) => updateCompetitionSettings(comp.id, { allows_image: e.target.checked })}
                              className="w-4 h-4 rounded"
                            />
                            <span className="text-sm flex items-center gap-1">
                              <Image className="w-4 h-4" /> Allow Photo
                            </span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={comp.allows_video || false}
                              onChange={(e) => updateCompetitionSettings(comp.id, { allows_video: e.target.checked })}
                              className="w-4 h-4 rounded"
                            />
                            <span className="text-sm flex items-center gap-1">
                              <Video className="w-4 h-4" /> Allow Video
                            </span>
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Activities Corner Tab */}
          <TabsContent value="activity-posts">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Create Post Form */}
              <Card className="rounded-2xl">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-amber-500" />
                    Create Activity Post
                  </CardTitle>
                  <CardDescription>Add updates for Valentine's Day, Women's Day, Weekly Updates, etc.</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={createActivityPost} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Category</Label>
                      <select
                        className="w-full p-2 border rounded-xl"
                        value={newActivityPost.category}
                        onChange={(e) => setNewActivityPost({ ...newActivityPost, category: e.target.value })}
                        data-testid="activity-post-category-select"
                      >
                        <option value="general">General</option>
                        <option value="valentines">Valentine's Day</option>
                        <option value="womens_day">Women's Day</option>
                        <option value="weekly_update">Weekly Update</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>Title</Label>
                      <Input
                        value={newActivityPost.title}
                        onChange={(e) => setNewActivityPost({ ...newActivityPost, title: e.target.value })}
                        placeholder="Post title"
                        className="rounded-xl"
                        data-testid="activity-post-title-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Content</Label>
                      <Textarea
                        value={newActivityPost.content}
                        onChange={(e) => setNewActivityPost({ ...newActivityPost, content: e.target.value })}
                        placeholder="Write your message..."
                        className="rounded-xl min-h-[100px]"
                        data-testid="activity-post-content-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Photo/Video (Optional)</Label>
                      <Input
                        type="file"
                        accept="image/*,video/*"
                        onChange={handleActivityMediaChange}
                        className="rounded-xl"
                        data-testid="activity-post-media-input"
                      />
                      {newActivityPost.media_data && (
                        <p className="text-xs text-green-600">Media selected: {newActivityPost.media_type}</p>
                      )}
                    </div>
                    <Button type="submit" className="w-full bg-amber-600 hover:bg-amber-700 rounded-full" data-testid="create-activity-post-btn">
                      Post Update
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Existing Posts */}
              <Card className="rounded-2xl">
                <CardHeader>
                  <CardTitle>Activity Posts ({activityPosts.length})</CardTitle>
                  <CardDescription>Manage existing posts - will show in Dashboard</CardDescription>
                </CardHeader>
                <CardContent>
                  {activityPosts.length === 0 ? (
                    <p className="text-slate-500 text-center py-8">No posts yet. Create your first one!</p>
                  ) : (
                    <div className="space-y-3 max-h-[400px] overflow-y-auto">
                      {activityPosts.map((post) => (
                        <div key={post.id} className="p-4 bg-slate-50 rounded-xl" data-testid={`admin-activity-post-${post.id}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge className="text-xs capitalize bg-amber-100 text-amber-700">
                                  {post.category.replace("_", " ")}
                                </Badge>
                                {post.media_type && (
                                  <Badge variant="outline" className="text-xs">
                                    {post.media_type === "video" ? <Video className="w-3 h-3 mr-1" /> : <Image className="w-3 h-3 mr-1" />}
                                    {post.media_type}
                                  </Badge>
                                )}
                              </div>
                              <h4 className="font-semibold text-slate-800">{post.title}</h4>
                              <p className="text-sm text-slate-500 line-clamp-2">{post.content}</p>
                              <p className="text-xs text-slate-400 mt-1">
                                {new Date(post.created_at).toLocaleString()} by {post.created_by_name}
                              </p>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => deleteActivityPost(post.id)}
                              className="text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Messages Monitor Tab */}
          <TabsContent value="messages">
            <Card className="rounded-2xl">
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <MessageCircle className="w-5 h-5 text-teal-600" />
                      Messages Monitor
                    </CardTitle>
                    <CardDescription>View all conversations between students and co-admins</CardDescription>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <select
                      value={messageDistrictFilter}
                      onChange={(e) => setMessageDistrictFilter(e.target.value)}
                      className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                    >
                      <option value="all">All Districts</option>
                      {districts.map((d) => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                    <select
                      value={messageCoAdminFilter || "all"}
                      onChange={(e) => setMessageCoAdminFilter(e.target.value)}
                      className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                    >
                      <option value="all">All Co-Admins</option>
                      {coAdmins.map((ca) => (
                        <option key={ca.id} value={ca.id}>{ca.name} ({ca.district})</option>
                      ))}
                    </select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {allMessages.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <MessageCircle className="w-16 h-16 mx-auto mb-4 opacity-20" />
                    <p className="text-lg">No messages yet</p>
                    <p className="text-sm">Conversations between students and co-admins will appear here</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[600px] overflow-y-auto">
                    {allMessages
                      .filter(msg => {
                        const districtMatch = messageDistrictFilter === "all" || msg.district === messageDistrictFilter;
                        const coAdminMatch = messageCoAdminFilter === "all" || 
                          msg.sender_id === messageCoAdminFilter || 
                          msg.receiver_id === messageCoAdminFilter;
                        return districtMatch && coAdminMatch;
                      })
                      .map((msg) => (
                        <div 
                          key={msg.id} 
                          className="p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                          data-testid={`admin-message-${msg.id}`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                                msg.sender_role === "co_admin" ? 'bg-purple-500' : 'bg-teal-500'
                              }`}>
                                {msg.sender_name?.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">{msg.sender_name}</span>
                                  <Badge className={`text-xs ${
                                    msg.sender_role === "co_admin" ? 'bg-purple-100 text-purple-700' : 'bg-teal-100 text-teal-700'
                                  }`}>
                                    {msg.sender_role === "co_admin" ? "Co-Admin" : "Student"}
                                  </Badge>
                                  <ArrowRight className="w-4 h-4 text-slate-400" />
                                  <span className="font-medium">{msg.receiver_name}</span>
                                  <Badge className={`text-xs ${
                                    msg.receiver_role === "co_admin" ? 'bg-purple-100 text-purple-700' : 'bg-teal-100 text-teal-700'
                                  }`}>
                                    {msg.receiver_role === "co_admin" ? "Co-Admin" : "Student"}
                                  </Badge>
                                </div>
                                <p className="text-sm text-slate-600 mt-1">{msg.content}</p>
                                <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                                  <span>{new Date(msg.timestamp).toLocaleString()}</span>
                                  {msg.district && (
                                    <Badge variant="outline" className="text-xs">{msg.district}</Badge>
                                  )}
                                  {msg.is_read ? (
                                    <span className="text-green-500">Read</span>
                                  ) : (
                                    <span className="text-amber-500">Unread</span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => openEditMessage(msg)}
                                className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-lg"
                                title="Edit message"
                                data-testid={`edit-message-${msg.id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => deleteMessage(msg.id)}
                                className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"
                                title="Delete message"
                                data-testid={`delete-message-${msg.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
                
                {/* Summary Stats */}
                <div className="mt-6 pt-6 border-t grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-teal-50 rounded-xl">
                    <p className="text-2xl font-bold text-teal-700">{allMessages.length}</p>
                    <p className="text-sm text-teal-600">Total Messages</p>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-xl">
                    <p className="text-2xl font-bold text-purple-700">
                      {new Set(allMessages.map(m => m.sender_id)).size}
                    </p>
                    <p className="text-sm text-purple-600">Active Users</p>
                  </div>
                  <div className="text-center p-4 bg-amber-50 rounded-xl">
                    <p className="text-2xl font-bold text-amber-700">
                      {allMessages.filter(m => !m.is_read).length}
                    </p>
                    <p className="text-sm text-amber-600">Unread</p>
                  </div>
                  <div className="text-center p-4 bg-slate-50 rounded-xl">
                    <p className="text-2xl font-bold text-slate-700">
                      {new Set(allMessages.map(m => m.district).filter(Boolean)).size}
                    </p>
                    <p className="text-sm text-slate-600">Active Districts</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="duna100">
            <Duna100Admin />
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <Card className="rounded-2xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5 text-slate-600" />
                  App Settings
                </CardTitle>
                <CardDescription>Manage application settings</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Payment Settings */}
                <div className="p-6 bg-slate-50 rounded-xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${paymentEnabled ? 'bg-green-100' : 'bg-red-100'}`}>
                        <CreditCard className={`w-6 h-6 ${paymentEnabled ? 'text-green-600' : 'text-red-600'}`} />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg">Online Payments</h3>
                        <p className="text-slate-500">
                          {paymentEnabled ? 'Payments are currently enabled' : 'Payments are currently disabled'}
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={togglePayment}
                      className={`rounded-full ${paymentEnabled ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'}`}
                      data-testid="toggle-payment-btn"
                    >
                      {paymentEnabled ? 'Disable Payments' : 'Enable Payments'}
                    </Button>
                  </div>
                </div>

                {/* Status Indicators */}
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="p-4 bg-white border rounded-xl text-center">
                    <div className={`w-3 h-3 rounded-full mx-auto mb-2 ${paymentEnabled ? 'bg-green-500' : 'bg-red-500'}`}></div>
                    <p className="text-sm font-medium">Payment Gateway</p>
                    <p className="text-xs text-slate-500">{paymentEnabled ? 'Active' : 'Inactive'}</p>
                  </div>
                  <div className="p-4 bg-white border rounded-xl text-center">
                    <div className="w-3 h-3 rounded-full mx-auto mb-2 bg-green-500"></div>
                    <p className="text-sm font-medium">Chat System</p>
                    <p className="text-xs text-slate-500">Active</p>
                  </div>
                  <div className="p-4 bg-white border rounded-xl text-center">
                    <div className="w-3 h-3 rounded-full mx-auto mb-2 bg-green-500"></div>
                    <p className="text-sm font-medium">Course System</p>
                    <p className="text-xs text-slate-500">Active</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Edit Student Modal */}
        {editingStudent && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md rounded-2xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Edit className="w-5 h-5 text-teal-600" />
                  Edit Student
                </CardTitle>
                <CardDescription>Update student details</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={saveStudentChanges} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Full Name</Label>
                    <Input
                      placeholder="Student's full name"
                      value={editStudentData.name}
                      onChange={(e) => setEditStudentData({...editStudentData, name: e.target.value})}
                      className="rounded-xl"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      placeholder="Phone number"
                      value={editStudentData.phone}
                      onChange={(e) => setEditStudentData({...editStudentData, phone: e.target.value})}
                      className="rounded-xl"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      placeholder="email@example.com"
                      value={editStudentData.email}
                      onChange={(e) => setEditStudentData({...editStudentData, email: e.target.value})}
                      className="rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>District</Label>
                    <select
                      value={editStudentData.district}
                      onChange={(e) => setEditStudentData({...editStudentData, district: e.target.value})}
                      className="w-full p-3 border rounded-xl bg-white"
                    >
                      {districts.map((d) => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>User Type</Label>
                    <select
                      value={editStudentData.user_type}
                      onChange={(e) => setEditStudentData({...editStudentData, user_type: e.target.value})}
                      className="w-full p-3 border rounded-xl bg-white"
                    >
                      <option value="woman">Educated Unemployed</option>
                      <option value="girl">Dropout</option>
                      <option value="general">General</option>
                    </select>
                  </div>
                  <div className="flex gap-3 pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1 rounded-full"
                      onClick={() => setEditingStudent(null)}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" className="flex-1 bg-teal-600 hover:bg-teal-700 rounded-full">
                      Save Changes
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Edit Message Modal */}
        {editingMessage && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md rounded-2xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Edit className="w-5 h-5 text-blue-600" />
                  Edit Message
                </CardTitle>
                <CardDescription>
                  From: {editingMessage.sender_name} → To: {editingMessage.receiver_name}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Message Content</Label>
                    <Textarea
                      placeholder="Enter message content..."
                      value={editMessageContent}
                      onChange={(e) => setEditMessageContent(e.target.value)}
                      className="rounded-xl min-h-[100px]"
                      required
                    />
                  </div>
                  <div className="flex gap-3 pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1 rounded-full"
                      onClick={() => {
                        setEditingMessage(null);
                        setEditMessageContent("");
                      }}
                    >
                      Cancel
                    </Button>
                    <Button 
                      onClick={saveMessageChanges} 
                      className="flex-1 bg-blue-600 hover:bg-blue-700 rounded-full"
                    >
                      Save Changes
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

// ============ DUNA 100 PAGE ============
const Duna100 = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [memberData, setMemberData] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [soldNumbers, setSoldNumbers] = useState([]);
  const [results, setResults] = useState(null);
  
  // Member login form
  const [loginForm, setLoginForm] = useState({
    name: "",
    phone: "",
    password: ""
  });
  
  // Supporter form (for agents)
  const [supporterForm, setSupporterForm] = useState({
    name: "",
    phone: "",
    digit1: "",
    digit2: "",
    amount: ""
  });
  
  const [loading, setLoading] = useState(false);
  const [supporterSuccess, setSupporterSuccess] = useState(null);

  // Fetch sold numbers and results on load
  useEffect(() => {
    fetchSoldNumbers();
    fetchResults();
  }, []);

  const fetchSoldNumbers = async () => {
    try {
      const response = await axios.get(`${API}/duna100-entries/numbers`);
      setSoldNumbers(response.data.numbers || []);
    } catch (error) {
      console.error("Error fetching sold numbers:", error);
    }
  };

  const fetchResults = async () => {
    try {
      const response = await axios.get(`${API}/duna100-results/latest`);
      if (response.data) {
        setResults(response.data);
      }
    } catch (error) {
      console.error("Error fetching results:", error);
    }
  };

  // Load Razorpay script
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  // Handle member login
  const handleMemberLogin = async (e) => {
    e.preventDefault();
    if (!loginForm.name || !loginForm.phone || !loginForm.password) {
      alert("Please fill in all fields");
      return;
    }
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setIsLoggedIn(true);
      setMemberData({
        name: loginForm.name,
        phone: loginForm.phone
      });
      fetchSoldNumbers();
    }, 1000);
  };

  // Check if number is already sold
  const isNumberSold = (number) => {
    return soldNumbers.includes(number);
  };

  // Handle supporter registration with payment
  const handleSupporterSubmit = async (e) => {
    e.preventDefault();
    const { name, phone, digit1, digit2, amount } = supporterForm;
    
    if (!name || !phone || !digit1 || !digit2 || !amount) {
      alert("Please fill in all fields including amount");
      return;
    }
    
    const digits = [digit1, digit2];
    if (!digits.every(d => /^[0-9]$/.test(d))) {
      alert("Please enter single digits (0-9) in each box");
      return;
    }
    
    const dunaNumber = `${digit1}${digit2}`;
    
    // Check if number already sold
    if (isNumberSold(dunaNumber)) {
      alert(`Number ${dunaNumber} is already sold. Please choose a different number.`);
      return;
    }

    const amountValue = parseInt(amount);
    if (amountValue < 1) {
      alert("Please enter a valid amount");
      return;
    }
    
    setLoading(true);
    
    const scriptLoaded = await loadRazorpayScript();
    if (!scriptLoaded) {
      alert("Failed to load payment gateway. Please try again.");
      setLoading(false);
      return;
    }

    try {
      const orderResponse = await axios.post(`${API}/payments/create-order`, {
        amount: amountValue * 100,
        currency: "INR",
        name: name,
        phone: phone,
        purpose: "duna100"
      });

      const options = {
        key: orderResponse.data.key_id,
        amount: orderResponse.data.amount,
        currency: orderResponse.data.currency,
        name: "NokMechik - Duna 100",
        description: `Duna 100 Number: ${dunaNumber}`,
        order_id: orderResponse.data.order_id,
        handler: async (response) => {
          try {
            await axios.post(`${API}/payments/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });
            
            await axios.post(`${API}/duna100-entries`, {
              supporter_name: name,
              supporter_phone: phone,
              duna_number: dunaNumber,
              amount: amountValue,
              agent_name: memberData?.name || "",
              agent_phone: memberData?.phone || "",
              payment_id: response.razorpay_payment_id
            });
            
            const today = new Date().toLocaleDateString('en-IN');
            setSupporterSuccess({
              name,
              phone,
              number: dunaNumber,
              amount: amountValue,
              date: today,
              message: `${dunaNumber} is your Duna 100 booked on ${today}. Thank you for supporting Nokmechik. Mitela.`
            });
            
            setSupporterForm({
              name: "",
              phone: "",
              digit1: "",
              digit2: "",
              amount: ""
            });
            
            fetchSoldNumbers();
          } catch (error) {
            console.error("Payment verification failed:", error);
            alert("Payment verification failed. Please contact support.");
          }
        },
        prefill: {
          name: name,
          contact: phone
        },
        config: {
          display: {
            blocks: {
              utib: {
                name: "Pay using UPI",
                instruments: [
                  { method: "upi" }
                ]
              },
              other: {
                name: "Other Payment Methods",
                instruments: [
                  { method: "card" },
                  { method: "netbanking" },
                  { method: "wallet" }
                ]
              }
            },
            sequence: ["block.utib", "block.other"],
            preferences: {
              show_default_blocks: false
            }
          }
        },
        theme: {
          color: "#db2777"
        }
      };

      const razorpayInstance = new window.Razorpay(options);
      razorpayInstance.open();
    } catch (error) {
      console.error("Error creating order:", error);
      alert("Failed to initiate payment. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDigitChange = (value, field, nextField) => {
    if (value.length <= 1 && /^[0-9]?$/.test(value)) {
      setSupporterForm({ ...supporterForm, [field]: value });
      if (value && nextField) {
        document.getElementById(nextField)?.focus();
      }
    }
  };

  const currentNumber = `${supporterForm.digit1 || '_'}${supporterForm.digit2 || '_'}`;
  const isCurrentNumberSold = supporterForm.digit1 && supporterForm.digit2 && 
    isNumberSold(`${supporterForm.digit1}${supporterForm.digit2}`);

  // Results Display Component
  const ResultsDisplay = () => {
    if (!results) return null;
    
    return (
      <div className="absolute top-4 right-4 bg-white rounded-2xl shadow-lg p-4 min-w-[280px] z-10" data-testid="results-display">
        <h3 className="text-sm font-bold text-slate-800 mb-3 text-center border-b pb-2">Results</h3>
        <div className="space-y-3 text-sm">
          <div className="flex justify-between items-center">
            <span className="text-amber-600 font-medium">1st:</span>
            <div className="text-right">
              <span className="font-bold text-lg">{results.first || '--'}</span>
              {results.first_bonus && <span className="ml-2 text-amber-500 bg-amber-50 px-2 py-0.5 rounded text-xs">Bonus: {results.first_bonus}</span>}
            </div>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-slate-500 font-medium">2nd:</span>
            <div className="text-right">
              <span className="font-bold text-lg">{results.second || '--'}</span>
              {results.second_bonus && <span className="ml-2 text-slate-400 bg-slate-50 px-2 py-0.5 rounded text-xs">Bonus: {results.second_bonus}</span>}
            </div>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-orange-700 font-medium">3rd:</span>
            <div className="text-right">
              <span className="font-bold text-lg">{results.third || '--'}</span>
              {results.third_bonus && <span className="ml-2 text-orange-500 bg-orange-50 px-2 py-0.5 rounded text-xs">Bonus: {results.third_bonus}</span>}
            </div>
          </div>
          {results.consolations && results.consolations.length > 0 && (
            <div className="border-t pt-2 mt-2">
              <p className="text-xs text-slate-500 mb-1">Consolation:</p>
              <div className="grid grid-cols-2 gap-1 text-xs">
                {results.consolations.map((num, i) => (
                  <div key={i} className="flex items-center gap-1">
                    <span className="font-mono font-medium">{num}</span>
                    {results.consolation_bonuses?.[i] && (
                      <span className="text-purple-500 text-[10px]">({results.consolation_bonuses[i]})</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Member Login Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-transparent relative" data-testid="duna100-login-page">
        <Navbar />
        <ResultsDisplay />
        <div className="pt-24 pb-16 max-w-md mx-auto px-6">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-pink-100 text-pink-700 px-4 py-2 rounded-full mb-4">
              <Gift className="w-5 h-5" />
              <span className="font-medium">Member Access</span>
            </div>
            <h1 className="text-3xl font-bold text-slate-800 mb-2">Duna 100</h1>
            <p className="text-slate-600">Login with your NokMechik credentials</p>
          </div>

          <Card className="rounded-3xl shadow-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-pink-600" />
                Member Login
              </CardTitle>
              <CardDescription>Enter your registered details</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleMemberLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="member-name">Full Name <span className="text-red-500">*</span></Label>
                  <Input
                    id="member-name"
                    placeholder="Your registered name"
                    value={loginForm.name}
                    onChange={(e) => setLoginForm({ ...loginForm, name: e.target.value })}
                    className="rounded-xl"
                    required
                    data-testid="duna100-member-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="member-phone">
                    <Phone className="w-4 h-4 inline mr-1" />
                    Mobile Number <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="member-phone"
                    type="tel"
                    placeholder="Your registered mobile"
                    value={loginForm.phone}
                    onChange={(e) => setLoginForm({ ...loginForm, phone: e.target.value })}
                    className="rounded-xl"
                    required
                    data-testid="duna100-member-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="member-password">
                    <Lock className="w-4 h-4 inline mr-1" />
                    Password <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative">
                    <Input
                      id="member-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Your registered password"
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                      className="rounded-xl pr-10"
                      required
                      data-testid="duna100-member-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-pink-600 hover:bg-pink-700 rounded-full py-6 text-lg mt-4"
                  disabled={loading}
                  data-testid="duna100-login-btn"
                >
                  {loading ? "Logging in..." : "Login to Duna 100"}
                </Button>
              </form>
              <p className="text-center mt-6 text-slate-500 text-sm">
                Not a member? <Link to="/register" className="text-pink-600 hover:underline">Register first</Link>
              </p>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  // Supporter Success Screen
  if (supporterSuccess) {
    return (
      <div className="min-h-screen bg-transparent relative" data-testid="duna100-supporter-success">
        <Navbar />
        <ResultsDisplay />
        <div className="pt-24 pb-16 max-w-2xl mx-auto px-6 text-center">
          <div className="bg-white rounded-3xl shadow-xl p-12">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-800 mb-4">Registration Successful!</h1>
            
            <div className="bg-pink-50 rounded-2xl p-6 mb-6 text-left">
              <p className="text-sm text-slate-500 mb-2">Message sent to {supporterSuccess.phone}:</p>
              <p className="text-lg text-slate-800 font-medium">
                "{supporterSuccess.message}"
              </p>
            </div>
            
            <div className="grid grid-cols-4 gap-3 max-w-xs mx-auto mb-6">
              {supporterSuccess.number.split('').map((digit, i) => (
                <div key={i} className="w-16 h-16 bg-pink-100 rounded-xl flex items-center justify-center">
                  <span className="text-3xl font-bold text-pink-600">{digit}</span>
                </div>
              ))}
            </div>
            
            <p className="text-slate-600 mb-2">
              Registered for: <strong>{supporterSuccess.name}</strong>
            </p>
            <p className="text-slate-600 mb-6">
              Amount: <strong>₹{supporterSuccess.amount}</strong>
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                className="rounded-full bg-pink-600 hover:bg-pink-700"
                onClick={() => setSupporterSuccess(null)}
                data-testid="add-another-supporter-btn"
              >
                Add Another Supporter
              </Button>
              <Link to="/">
                <Button variant="outline" className="rounded-full">
                  Back to Home
                </Button>
              </Link>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Agent Dashboard (logged in)
  return (
    <div className="min-h-screen bg-transparent relative" data-testid="duna100-agent-page">
      <Navbar />
      <ResultsDisplay />
      <div className="pt-24 pb-16 max-w-4xl mx-auto px-6">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-800">Duna 100 Agent</h1>
            <p className="text-slate-600">Welcome, {memberData?.name}</p>
          </div>
          <Button 
            variant="outline" 
            className="rounded-full"
            onClick={() => {
              setIsLoggedIn(false);
              setMemberData(null);
              setLoginForm({ name: "", phone: "", password: "" });
            }}
            data-testid="duna100-logout-btn"
          >
            Logout
          </Button>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Add Supporter Form */}
          <Card className="rounded-3xl shadow-xl">
            <CardHeader className="bg-gradient-to-br from-pink-500/5 to-pink-500/10 rounded-t-3xl">
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-pink-600" />
                Add Supporter
              </CardTitle>
              <CardDescription>Register a new supporter with their Duna 100 number</CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <form onSubmit={handleSupporterSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="supporter-name">Supporter's Name <span className="text-red-500">*</span></Label>
                  <Input
                    id="supporter-name"
                    placeholder="Enter supporter's full name"
                    value={supporterForm.name}
                    onChange={(e) => setSupporterForm({ ...supporterForm, name: e.target.value })}
                    className="rounded-xl"
                    required
                    data-testid="supporter-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="supporter-phone">
                    <Phone className="w-4 h-4 inline mr-1" />
                    Supporter's Mobile <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="supporter-phone"
                    type="tel"
                    placeholder="Enter supporter's mobile number"
                    value={supporterForm.phone}
                    onChange={(e) => setSupporterForm({ ...supporterForm, phone: e.target.value })}
                    className="rounded-xl"
                    required
                    data-testid="supporter-phone-input"
                  />
                </div>
                
                {/* 2 Digit Number Selection */}
                <div className="space-y-2">
                  <Label>Choose 2-Digit Number <span className="text-red-500">*</span></Label>
                  <div className="grid grid-cols-2 gap-4 max-w-[200px] mx-auto">
                    <Input
                      id="digit1"
                      type="text"
                      maxLength={1}
                      placeholder="0"
                      value={supporterForm.digit1}
                      onChange={(e) => handleDigitChange(e.target.value, 'digit1', 'digit2')}
                      className={`rounded-xl text-center text-3xl font-bold h-20 ${isCurrentNumberSold ? 'border-red-500 bg-red-50' : ''}`}
                      data-testid="digit1-input"
                    />
                    <Input
                      id="digit2"
                      type="text"
                      maxLength={1}
                      placeholder="0"
                      value={supporterForm.digit2}
                      onChange={(e) => handleDigitChange(e.target.value, 'digit2', null)}
                      className={`rounded-xl text-center text-3xl font-bold h-20 ${isCurrentNumberSold ? 'border-red-500 bg-red-50' : ''}`}
                      data-testid="digit2-input"
                    />
                  </div>
                  {isCurrentNumberSold && (
                    <p className="text-red-500 text-sm text-center font-medium">
                      Number {currentNumber} is already sold!
                    </p>
                  )}
                  <p className="text-xs text-slate-500 text-center">Enter single digit (0-9) in each box</p>
                </div>

                {/* Manual Amount Input */}
                <div className="space-y-2">
                  <Label htmlFor="support-amount">
                    <IndianRupee className="w-4 h-4 inline mr-1" />
                    Support Amount <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="support-amount"
                    type="number"
                    min="1"
                    placeholder="Enter amount in ₹"
                    value={supporterForm.amount}
                    onChange={(e) => setSupporterForm({ ...supporterForm, amount: e.target.value })}
                    className="rounded-xl"
                    required
                    data-testid="support-amount-input"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-pink-600 hover:bg-pink-700 rounded-full py-6 text-lg mt-4"
                  disabled={loading || isCurrentNumberSold}
                  data-testid="register-supporter-btn"
                >
                  {loading ? "Processing..." : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Register & Pay {supporterForm.amount ? `₹${supporterForm.amount}` : ''}
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Info Cards */}
          <div className="space-y-6">
            <Card className="rounded-2xl bg-gradient-to-br from-pink-500/5 to-pink-500/10">
              <CardHeader>
                <CardTitle className="text-lg">How It Works</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-pink-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-pink-600 font-bold">1</span>
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">Add Supporter Details</p>
                    <p className="text-sm text-slate-600">Enter name and mobile number</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-pink-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-pink-600 font-bold">2</span>
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">Choose 4-Digit Number</p>
                    <p className="text-sm text-slate-600">Unique number (not sold before)</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-pink-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-pink-600 font-bold">3</span>
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">Enter Support Amount</p>
                    <p className="text-sm text-slate-600">Any amount supporter wants to give</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-pink-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-pink-600 font-bold">4</span>
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">Complete Payment</p>
                    <p className="text-sm text-slate-600">Pay via Razorpay (GPay/UPI)</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-2xl">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Mail className="w-5 h-5 text-pink-600" />
                  <span className="font-medium text-slate-800">Confirmation Message</span>
                </div>
                <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg italic">
                  "XXXX is your Duna 100 booked on (date). Thank you for supporting Nokmechik. Mitela."
                </p>
              </CardContent>
            </Card>

            <Card className="rounded-2xl border-pink-200 bg-pink-50">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-2">
                  <Trophy className="w-5 h-5 text-pink-600" />
                  <span className="font-medium text-slate-800">Agent: {memberData?.name}</span>
                </div>
                <p className="text-sm text-slate-600">
                  Phone: {memberData?.phone}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// ============ PAYMENT PAGE ============
const Payment = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    amount: 100,
    purpose: "donation"
  });
  const [loading, setLoading] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentEnabled, setPaymentEnabled] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);

  const predefinedAmounts = [100, 200, 500, 1000, 2000, 5000];

  // Check if payment is enabled
  useEffect(() => {
    const checkPaymentStatus = async () => {
      try {
        const res = await axios.get(`${API}/settings/payment`);
        setPaymentEnabled(res.data.enabled);
      } catch (error) {
        console.error("Error checking payment status:", error);
        setPaymentEnabled(false);
      } finally {
        setCheckingStatus(false);
      }
    };
    checkPaymentStatus();
  }, []);

  // Load Razorpay script
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    
    if (!paymentEnabled) {
      alert("Payment is currently disabled. Please try again later.");
      return;
    }
    
    if (!formData.name || !formData.phone || !formData.amount) {
      alert("Please fill in required fields");
      return;
    }

    setLoading(true);
    
    // Load Razorpay script
    const scriptLoaded = await loadRazorpayScript();
    if (!scriptLoaded) {
      alert("Failed to load payment gateway. Please try again.");
      setLoading(false);
      return;
    }

    try {
      // Create order
      const orderResponse = await axios.post(`${API}/payments/create-order`, {
        amount: formData.amount * 100, // Convert to paise
        currency: "INR",
        name: formData.name,
        phone: formData.phone,
        email: formData.email || null,
        purpose: formData.purpose
      });

      const options = {
        key: orderResponse.data.key_id,
        amount: orderResponse.data.amount,
        currency: orderResponse.data.currency,
        name: "NokMechik",
        description: `${formData.purpose === 'donation' ? 'Donation' : 'Payment'} for Women & Girls Empowerment`,
        order_id: orderResponse.data.order_id,
        handler: async (response) => {
          try {
            // Verify payment
            await axios.post(`${API}/payments/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });
            setPaymentSuccess(true);
          } catch (error) {
            console.error("Payment verification failed:", error);
            alert("Payment verification failed. Please contact support.");
          }
        },
        prefill: {
          name: formData.name,
          email: formData.email || "",
          contact: formData.phone
        },
        config: {
          display: {
            blocks: {
              utib: {
                name: "Pay using UPI",
                instruments: [
                  { method: "upi" }
                ]
              },
              other: {
                name: "Other Payment Methods",
                instruments: [
                  { method: "card" },
                  { method: "netbanking" },
                  { method: "wallet" }
                ]
              }
            },
            sequence: ["block.utib", "block.other"],
            preferences: {
              show_default_blocks: false
            }
          }
        },
        theme: {
          color: "#c96442"
        }
      };

      const razorpayInstance = new window.Razorpay(options);
      razorpayInstance.open();
    } catch (error) {
      console.error("Error creating order:", error);
      alert("Failed to initiate payment. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (checkingStatus) {
    return (
      <div className="min-h-screen bg-transparent flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-terracotta border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!paymentEnabled) {
    return (
      <div className="min-h-screen bg-transparent" data-testid="payment-disabled">
        <Navbar />
        <div className="pt-24 pb-16 max-w-2xl mx-auto px-6 text-center">
          <div className="bg-white rounded-3xl shadow-xl p-12">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CreditCard className="w-10 h-10 text-amber-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-800 mb-4">Payment Temporarily Unavailable</h1>
            <p className="text-lg text-slate-600 mb-8">
              Online payments are currently disabled. Please check back later or contact the administrator.
            </p>
            <Link to="/">
              <Button className="rounded-full bg-terracotta hover:bg-terracotta/90" data-testid="back-home-btn">
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (paymentSuccess) {
    return (
      <div className="min-h-screen bg-transparent" data-testid="payment-success">
        <Navbar />
        <div className="pt-24 pb-16 max-w-2xl mx-auto px-6 text-center">
          <div className="bg-white rounded-3xl shadow-xl p-12">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-800 mb-4">Payment Successful!</h1>
            <p className="text-lg text-slate-600 mb-8">
              Thank you for your generous contribution to empowering women and girls.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/">
                <Button className="rounded-full" data-testid="back-home-btn">
                  Back to Home
                </Button>
              </Link>
              <Button 
                variant="outline" 
                className="rounded-full"
                onClick={() => setPaymentSuccess(false)}
                data-testid="make-another-payment-btn"
              >
                Make Another Payment
              </Button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-transparent" data-testid="payment-page">
      <Navbar />
      <div className="pt-24 pb-16 max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full mb-4">
            <CreditCard className="w-5 h-5" />
            <span className="font-medium">Secure Payment</span>
          </div>
          <h1 className="text-4xl font-bold text-slate-800 mb-4">Support Our Mission</h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Your contribution helps empower women with skills and provide education to dropout girls.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Payment Form */}
          <Card className="rounded-3xl shadow-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <IndianRupee className="w-5 h-5 text-green-600" />
                Make a Payment
              </CardTitle>
              <CardDescription>Fill in your details to proceed</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePayment} className="space-y-4" autoComplete="off">
                <div className="space-y-2">
                  <Label htmlFor="pay-name">Full Name <span className="text-red-500">*</span></Label>
                  <Input
                    id="pay-name"
                    placeholder="Enter your full name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="rounded-xl"
                    required
                    autoComplete="off"
                    data-testid="payment-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pay-phone">
                    <Phone className="w-4 h-4 inline mr-1" />
                    Phone Number <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="pay-phone"
                    type="tel"
                    placeholder="Enter your phone number"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="rounded-xl"
                    required
                    autoComplete="off"
                    data-testid="payment-phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pay-email" className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Email <span className="text-xs text-slate-400">(Optional)</span>
                  </Label>
                  <Input
                    id="pay-email"
                    type="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="rounded-xl"
                    autoComplete="off"
                    data-testid="payment-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Purpose</Label>
                  <select
                    className="w-full p-3 border rounded-xl"
                    value={formData.purpose}
                    onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                    data-testid="payment-purpose-select"
                  >
                    <option value="donation">Donation</option>
                    <option value="course_fee">Course Fee</option>
                    <option value="competition_fee">Competition Fee</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Select Amount (₹) <span className="text-red-500">*</span></Label>
                  <div className="grid grid-cols-3 gap-2">
                    {predefinedAmounts.map((amt) => (
                      <Button
                        key={amt}
                        type="button"
                        variant={formData.amount === amt ? "default" : "outline"}
                        className={`rounded-xl ${formData.amount === amt ? 'bg-green-600 hover:bg-green-700' : ''}`}
                        onClick={() => setFormData({ ...formData, amount: amt })}
                        data-testid={`amount-${amt}-btn`}
                      >
                        ₹{amt}
                      </Button>
                    ))}
                  </div>
                  <div className="mt-3">
                    <Label htmlFor="custom-amount">Or enter custom amount</Label>
                    <Input
                      id="custom-amount"
                      type="number"
                      min="1"
                      placeholder="Enter amount in ₹"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: parseInt(e.target.value) || 0 })}
                      className="rounded-xl mt-1"
                      data-testid="payment-custom-amount-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-green-600 hover:bg-green-700 rounded-full py-6 text-lg mt-4"
                  disabled={loading}
                  data-testid="pay-now-btn"
                >
                  {loading ? (
                    "Processing..."
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Pay ₹{formData.amount || 0}
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Info Card */}
          <div className="space-y-6">
            <Card className="rounded-2xl bg-gradient-to-br from-terracotta/5 to-terracotta/10">
              <CardHeader>
                <CardTitle className="text-lg">Why Support Us?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-terracotta/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <GraduationCap className="w-4 h-4 text-terracotta" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">Education for Girls</p>
                    <p className="text-sm text-slate-600">Help dropout girls continue their education</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-terracotta/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Briefcase className="w-4 h-4 text-terracotta" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">Skills for Women</p>
                    <p className="text-sm text-slate-600">Support vocational training programs</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-terracotta/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Heart className="w-4 h-4 text-terracotta" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">Community Impact</p>
                    <p className="text-sm text-slate-600">Every contribution makes a difference</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-2xl">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Lock className="w-5 h-5 text-green-600" />
                  <span className="font-medium text-slate-800">Secure Payment</span>
                </div>
                <p className="text-sm text-slate-600">
                  All payments are processed securely through Razorpay. Your financial information is encrypted and safe.
                </p>
                <div className="flex items-center gap-2 mt-4">
                  <img src="https://razorpay.com/favicon.png" alt="Razorpay" className="w-6 h-6" />
                  <span className="text-sm text-slate-500">Powered by Razorpay</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// ============ COURSES PAGE ============
const Courses = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [courses, setCourses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newCourse, setNewCourse] = useState({ title: "", description: "" });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    const token = localStorage.getItem("token");
    
    if (!storedUser || !token) {
      navigate("/login");
      return;
    }
    
    try {
      const userData = JSON.parse(storedUser);
      setUser(userData);
      fetchData(token, userData.district);
    } catch (e) {
      navigate("/login");
    }
  }, [navigate]);

  const fetchData = async (token, district) => {
    try {
      const [coursesRes, enrollmentsRes] = await Promise.all([
        axios.get(`${API}/courses${district ? `?district=${encodeURIComponent(district)}` : ''}`),
        axios.get(`${API}/enrollments`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setCourses(coursesRes.data.courses || []);
      setEnrollments(enrollmentsRes.data.enrollments || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const createCourse = async (e) => {
    e.preventDefault();
    if (!newCourse.title.trim()) {
      alert("Please enter a course title");
      return;
    }
    
    const token = localStorage.getItem("token");
    setCreating(true);
    try {
      await axios.post(`${API}/courses`, {
        title: newCourse.title,
        description: newCourse.description,
        district: user.district
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewCourse({ title: "", description: "" });
      setShowCreateForm(false);
      fetchData(token, user?.district);
      alert("Course created successfully!");
    } catch (error) {
      alert(error.response?.data?.detail || "Failed to create course");
    } finally {
      setCreating(false);
    }
  };

  const deleteCourse = async (courseId, courseTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${courseTitle}"? This will also delete all lessons and assignments.`)) {
      return;
    }
    
    const token = localStorage.getItem("token");
    try {
      await axios.delete(`${API}/courses/${courseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData(token, user?.district);
      alert("Course deleted successfully!");
    } catch (error) {
      alert(error.response?.data?.detail || "Failed to delete course");
    }
  };

  const enrollInCourse = async (courseId) => {
    const token = localStorage.getItem("token");
    try {
      await axios.post(`${API}/enrollments/${courseId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData(token, user?.district);
    } catch (error) {
      alert(error.response?.data?.detail || "Failed to enroll");
    }
  };

  const isEnrolled = (courseId) => {
    return enrollments.some(e => e.course_id === courseId);
  };

  const getEnrollment = (courseId) => {
    return enrollments.find(e => e.course_id === courseId);
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-transparent" data-testid="courses-page">
      <Navbar />
      <div className="pt-24 pb-16 max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-800">Courses</h1>
            <p className="text-slate-600 mt-2">Explore and enroll in courses to start learning</p>
          </div>
          {(user.role === "admin" || user.role === "co_admin") && (
            <Button 
              onClick={() => setShowCreateForm(true)}
              className="bg-teal-600 hover:bg-teal-700 rounded-full"
              data-testid="create-course-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Course
            </Button>
          )}
        </div>

        {/* Create Course Modal */}
        {showCreateForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md rounded-2xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-teal-600" />
                  Create New Course
                </CardTitle>
                <CardDescription>Add a new course for {user.district || "all districts"}</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={createCourse} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Course Title *</Label>
                    <Input
                      placeholder="Enter course title"
                      value={newCourse.title}
                      onChange={(e) => setNewCourse({...newCourse, title: e.target.value})}
                      className="rounded-xl"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <textarea
                      placeholder="Describe the course content..."
                      value={newCourse.description}
                      onChange={(e) => setNewCourse({...newCourse, description: e.target.value})}
                      className="w-full p-3 border rounded-xl min-h-[100px] resize-none"
                    />
                  </div>
                  <div className="flex gap-3 pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1 rounded-full"
                      onClick={() => setShowCreateForm(false)}
                      disabled={creating}
                    >
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      className="flex-1 bg-teal-600 hover:bg-teal-700 rounded-full"
                      disabled={creating}
                    >
                      {creating ? "Creating..." : "Create Course"}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* My Enrolled Courses */}
        {enrollments.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-slate-700 mb-4">My Enrolled Courses</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {enrollments.map((enrollment) => (
                <Link key={enrollment.id} to={`/courses/${enrollment.course_id}`}>
                  <Card className="rounded-2xl hover:shadow-lg transition-shadow cursor-pointer border-teal-200 bg-gradient-to-br from-teal-50 to-white h-full">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                          <BookOpen className="w-6 h-6 text-teal-600" />
                        </div>
                        <Badge className="bg-teal-100 text-teal-700">{enrollment.progress}%</Badge>
                      </div>
                      <h3 className="font-bold text-slate-800 mb-1">{enrollment.course?.title}</h3>
                      <p className="text-sm text-slate-500 line-clamp-2">{enrollment.course?.description}</p>
                      <div className="mt-3 bg-slate-200 rounded-full h-2 overflow-hidden">
                        <div 
                          className="bg-teal-500 h-full transition-all duration-300" 
                          style={{ width: `${enrollment.progress}%` }}
                        />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Available Courses */}
        <div>
          <h2 className="text-lg font-semibold text-slate-700 mb-4">Available Courses</h2>
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
            </div>
          ) : courses.length === 0 ? (
            <Card className="rounded-2xl text-center p-8">
              <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">No Courses Yet</h3>
              <p className="text-slate-500">Courses will be available soon. Check back later!</p>
            </Card>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {courses.map((course) => (
                <Card key={course.id} className="rounded-2xl overflow-hidden hover:shadow-lg transition-shadow group" data-testid={`course-${course.id}`}>
                  {course.thumbnail_url ? (
                    <div className="aspect-video bg-slate-100 overflow-hidden relative">
                      <img src={course.thumbnail_url} alt={course.title} className="w-full h-full object-cover" />
                      {(user.role === "admin" || user.role === "co_admin") && (
                        <button
                          onClick={() => deleteCourse(course.id, course.title)}
                          className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Delete course"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="aspect-video bg-gradient-to-br from-teal-100 to-teal-200 flex items-center justify-center relative">
                      <BookOpen className="w-16 h-16 text-teal-400" />
                      {(user.role === "admin" || user.role === "co_admin") && (
                        <button
                          onClick={() => deleteCourse(course.id, course.title)}
                          className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Delete course"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  )}
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 mb-2">
                      {course.district && (
                        <Badge variant="outline" className="text-xs">{course.district}</Badge>
                      )}
                      <Badge className="bg-slate-100 text-slate-600 text-xs">
                        {course.lesson_count || 0} lessons
                      </Badge>
                    </div>
                    <h3 className="font-bold text-slate-800 mb-2">{course.title}</h3>
                    <p className="text-sm text-slate-500 line-clamp-2 mb-4">{course.description}</p>
                    <p className="text-xs text-slate-400 mb-3">By {course.created_by_name}</p>
                    
                    {isEnrolled(course.id) ? (
                      <Link to={`/courses/${course.id}`}>
                        <Button className="w-full bg-teal-600 hover:bg-teal-700 rounded-full">
                          <Play className="w-4 h-4 mr-2" />
                          Continue Learning
                        </Button>
                      </Link>
                    ) : (
                      <Button 
                        onClick={() => enrollInCourse(course.id)}
                        className="w-full bg-slate-800 hover:bg-slate-900 rounded-full"
                        data-testid={`enroll-${course.id}-btn`}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Enroll Now
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

// ============ COURSE DETAIL PAGE ============
const CourseDetail = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [course, setCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [enrollment, setEnrollment] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [mySubmissions, setMySubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("lessons");

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    const token = localStorage.getItem("token");
    
    if (!storedUser || !token) {
      navigate("/login");
      return;
    }
    
    try {
      const userData = JSON.parse(storedUser);
      setUser(userData);
      fetchCourseData(token, userData);
    } catch (e) {
      navigate("/login");
    }
  }, [courseId, navigate]);

  const fetchCourseData = async (token, userData) => {
    try {
      const [courseRes, enrollmentsRes, submissionsRes] = await Promise.all([
        axios.get(`${API}/courses/${courseId}`),
        axios.get(`${API}/enrollments`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/submissions/student`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setCourse(courseRes.data.course);
      setLessons(courseRes.data.lessons || []);
      setAssignments(courseRes.data.assignments || []);
      
      const myEnrollment = enrollmentsRes.data.enrollments?.find(e => e.course_id === courseId);
      setEnrollment(myEnrollment);
      
      setMySubmissions(submissionsRes.data.submissions?.filter(s => s.course_id === courseId) || []);
      
      if (courseRes.data.lessons?.length > 0) {
        setSelectedLesson(courseRes.data.lessons[0]);
      }
    } catch (error) {
      console.error("Error fetching course:", error);
    } finally {
      setLoading(false);
    }
  };

  const markLessonComplete = async (lessonId) => {
    const token = localStorage.getItem("token");
    try {
      await axios.patch(`${API}/enrollments/${courseId}/progress?lesson_id=${lessonId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchCourseData(token, user);
    } catch (error) {
      console.error("Error marking lesson complete:", error);
    }
  };

  const isLessonCompleted = (lessonId) => {
    return enrollment?.completed_lessons?.includes(lessonId);
  };

  const getSubmission = (assignmentId) => {
    return mySubmissions.find(s => s.assignment_id === assignmentId);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-transparent flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-transparent">
        <Navbar />
        <div className="pt-24 pb-16 max-w-7xl mx-auto px-4 text-center">
          <AlertCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-700">Course not found</h2>
          <Link to="/courses">
            <Button className="mt-4">Back to Courses</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-transparent" data-testid="course-detail-page">
      <Navbar />
      <div className="pt-20">
        {/* Course Header */}
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <Link to="/courses" className="inline-flex items-center gap-2 text-teal-200 hover:text-white mb-4">
              <ArrowLeft className="w-4 h-4" />
              Back to Courses
            </Link>
            <h1 className="text-2xl sm:text-3xl font-bold mb-2">{course.title}</h1>
            <p className="text-teal-100 mb-4">{course.description}</p>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span className="text-sm">{course.created_by_name}</span>
              </div>
              <div className="flex items-center gap-2">
                <BookOpen className="w-4 h-4" />
                <span className="text-sm">{lessons.length} Lessons</span>
              </div>
              <div className="flex items-center gap-2">
                <ClipboardList className="w-4 h-4" />
                <span className="text-sm">{assignments.length} Assignments</span>
              </div>
              {enrollment && (
                <Badge className="bg-white/20 text-white">
                  {enrollment.progress}% Complete
                </Badge>
              )}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6">
              <TabsTrigger value="lessons">Lessons</TabsTrigger>
              <TabsTrigger value="assignments">Assignments ({assignments.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="lessons">
              <div className="grid lg:grid-cols-3 gap-6">
                {/* Lesson List */}
                <div className="lg:col-span-1 space-y-2">
                  <h3 className="font-semibold text-slate-700 mb-3">Course Content</h3>
                  {lessons.length === 0 ? (
                    <p className="text-slate-500 text-sm">No lessons yet</p>
                  ) : (
                    lessons.map((lesson, index) => (
                      <button
                        key={lesson.id}
                        onClick={() => setSelectedLesson(lesson)}
                        className={`w-full p-3 rounded-xl flex items-center gap-3 transition-colors text-left ${
                          selectedLesson?.id === lesson.id 
                            ? 'bg-teal-100 border-2 border-teal-500' 
                            : 'bg-white hover:bg-slate-50 border-2 border-transparent'
                        }`}
                        data-testid={`lesson-${lesson.id}`}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          isLessonCompleted(lesson.id) 
                            ? 'bg-green-500 text-white' 
                            : 'bg-slate-200 text-slate-600'
                        }`}>
                          {isLessonCompleted(lesson.id) ? (
                            <CheckCircle className="w-4 h-4" />
                          ) : (
                            <span className="text-sm font-medium">{index + 1}</span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{lesson.title}</p>
                          <p className="text-xs text-slate-500 capitalize">{lesson.content_type}</p>
                        </div>
                      </button>
                    ))
                  )}
                </div>

                {/* Lesson Content */}
                <div className="lg:col-span-2">
                  {selectedLesson ? (
                    <Card className="rounded-2xl overflow-hidden">
                      <CardHeader className="bg-slate-50 border-b">
                        <div className="flex items-center justify-between">
                          <div>
                            <CardTitle>{selectedLesson.title}</CardTitle>
                            <CardDescription className="capitalize">{selectedLesson.content_type} Lesson</CardDescription>
                          </div>
                          {!isLessonCompleted(selectedLesson.id) && (
                            <Button
                              size="sm"
                              onClick={() => markLessonComplete(selectedLesson.id)}
                              className="bg-green-600 hover:bg-green-700"
                              data-testid="mark-complete-btn"
                            >
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Mark Complete
                            </Button>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent className="p-6">
                        {selectedLesson.content_type === "video" ? (
                          <div className="aspect-video bg-slate-900 rounded-xl overflow-hidden">
                            {selectedLesson.content.includes("youtube.com") || selectedLesson.content.includes("youtu.be") ? (
                              <iframe
                                src={selectedLesson.content.replace("watch?v=", "embed/")}
                                className="w-full h-full"
                                allowFullScreen
                                title={selectedLesson.title}
                              />
                            ) : (
                              <video controls className="w-full h-full">
                                <source src={selectedLesson.content} />
                              </video>
                            )}
                          </div>
                        ) : selectedLesson.content_type === "pdf" ? (
                          <div className="border rounded-xl p-4 bg-slate-50">
                            <a
                              href={selectedLesson.content}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-3 text-teal-600 hover:text-teal-700"
                            >
                              <FileText className="w-8 h-8" />
                              <span>Open PDF Document</span>
                            </a>
                          </div>
                        ) : (
                          <div className="prose max-w-none">
                            <p className="whitespace-pre-wrap">{selectedLesson.content}</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ) : (
                    <Card className="rounded-2xl text-center p-8">
                      <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500">Select a lesson to start learning</p>
                    </Card>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="assignments">
              {assignments.length === 0 ? (
                <Card className="rounded-2xl text-center p-8">
                  <ClipboardList className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No assignments yet</p>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {assignments.map((assignment) => {
                    const submission = getSubmission(assignment.id);
                    return (
                      <AssignmentCard 
                        key={assignment.id} 
                        assignment={assignment} 
                        submission={submission}
                        onSubmit={() => fetchCourseData(localStorage.getItem("token"), user)}
                      />
                    );
                  })}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

// ============ ASSIGNMENT CARD COMPONENT ============
const AssignmentCard = ({ assignment, submission, onSubmit }) => {
  const [showSubmit, setShowSubmit] = useState(false);
  const [content, setContent] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!content.trim()) return;
    
    const token = localStorage.getItem("token");
    setSubmitting(true);
    try {
      await axios.post(`${API}/submissions`, {
        assignment_id: assignment.id,
        content: content
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowSubmit(false);
      setContent("");
      onSubmit();
    } catch (error) {
      alert(error.response?.data?.detail || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="rounded-2xl" data-testid={`assignment-${assignment.id}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <ClipboardList className="w-5 h-5 text-amber-600" />
              <h3 className="font-semibold text-slate-800">{assignment.title}</h3>
            </div>
            <p className="text-sm text-slate-600 mb-3">{assignment.description}</p>
            {assignment.due_date && (
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Calendar className="w-4 h-4" />
                <span>Due: {new Date(assignment.due_date).toLocaleDateString()}</span>
              </div>
            )}
          </div>
          <div className="text-right">
            {submission ? (
              <div>
                {submission.grade !== null ? (
                  <div className="flex flex-col items-end">
                    <Badge className={`${
                      submission.grade >= 70 ? 'bg-green-100 text-green-700' :
                      submission.grade >= 50 ? 'bg-amber-100 text-amber-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      Grade: {submission.grade}/100
                    </Badge>
                    {submission.feedback && (
                      <p className="text-xs text-slate-500 mt-1 max-w-[200px] text-right">
                        {submission.feedback}
                      </p>
                    )}
                  </div>
                ) : (
                  <Badge className="bg-blue-100 text-blue-700">Submitted</Badge>
                )}
              </div>
            ) : (
              <Button
                onClick={() => setShowSubmit(true)}
                className="bg-amber-600 hover:bg-amber-700 rounded-full"
                size="sm"
                data-testid={`submit-assignment-${assignment.id}-btn`}
              >
                Submit
              </Button>
            )}
          </div>
        </div>

        {showSubmit && (
          <div className="mt-4 pt-4 border-t">
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Write your answer here..."
              className="min-h-[100px] rounded-xl mb-3"
              data-testid="assignment-content-input"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowSubmit(false)} size="sm">
                Cancel
              </Button>
              <Button 
                onClick={handleSubmit} 
                disabled={submitting || !content.trim()}
                className="bg-teal-600 hover:bg-teal-700"
                size="sm"
                data-testid="submit-answer-btn"
              >
                {submitting ? "Submitting..." : "Submit Answer"}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ============ COURSE EDITOR (for Admin/Co-Admin) ============
const CourseEditor = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [course, setCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("lessons");
  const [newLesson, setNewLesson] = useState({ title: "", content_type: "text", content: "", order: 0 });
  const [newAssignment, setNewAssignment] = useState({ title: "", description: "", due_date: "" });
  const [showAddLesson, setShowAddLesson] = useState(false);
  const [showAddAssignment, setShowAddAssignment] = useState(false);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    const token = localStorage.getItem("token");
    
    if (!storedUser || !token) {
      navigate("/login");
      return;
    }
    
    try {
      const userData = JSON.parse(storedUser);
      if (userData.role !== "admin" && userData.role !== "co_admin") {
        navigate("/dashboard");
        return;
      }
      setUser(userData);
      fetchCourseData(token);
    } catch (e) {
      navigate("/login");
    }
  }, [courseId, navigate]);

  const fetchCourseData = async (token) => {
    try {
      const courseRes = await axios.get(`${API}/courses/${courseId}`);
      setCourse(courseRes.data.course);
      setLessons(courseRes.data.lessons || []);
      setAssignments(courseRes.data.assignments || []);
      
      // Fetch submissions for all assignments
      const allSubmissions = [];
      for (const assignment of courseRes.data.assignments || []) {
        try {
          const subRes = await axios.get(`${API}/submissions/assignment/${assignment.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          allSubmissions.push(...(subRes.data.submissions || []).map(s => ({...s, assignment_title: assignment.title})));
        } catch (e) {
          // Ignore errors for individual submissions
        }
      }
      setSubmissions(allSubmissions);
    } catch (error) {
      console.error("Error fetching course:", error);
    } finally {
      setLoading(false);
    }
  };

  const addLesson = async (e) => {
    e.preventDefault();
    if (!newLesson.title || !newLesson.content) {
      alert("Please fill in lesson title and content");
      return;
    }
    
    const token = localStorage.getItem("token");
    try {
      await axios.post(`${API}/lessons`, {
        course_id: courseId,
        ...newLesson,
        order: lessons.length
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewLesson({ title: "", content_type: "text", content: "", order: 0 });
      setShowAddLesson(false);
      fetchCourseData(token);
    } catch (error) {
      alert(error.response?.data?.detail || "Error adding lesson");
    }
  };

  const deleteLesson = async (lessonId) => {
    if (!window.confirm("Are you sure you want to delete this lesson?")) return;
    
    const token = localStorage.getItem("token");
    try {
      await axios.delete(`${API}/lessons/${lessonId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchCourseData(token);
    } catch (error) {
      alert(error.response?.data?.detail || "Error deleting lesson");
    }
  };

  const addAssignment = async (e) => {
    e.preventDefault();
    if (!newAssignment.title || !newAssignment.description) {
      alert("Please fill in assignment title and description");
      return;
    }
    
    const token = localStorage.getItem("token");
    try {
      await axios.post(`${API}/assignments`, {
        course_id: courseId,
        ...newAssignment
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewAssignment({ title: "", description: "", due_date: "" });
      setShowAddAssignment(false);
      fetchCourseData(token);
    } catch (error) {
      alert(error.response?.data?.detail || "Error adding assignment");
    }
  };

  const deleteAssignment = async (assignmentId) => {
    if (!window.confirm("Are you sure you want to delete this assignment?")) return;
    
    const token = localStorage.getItem("token");
    try {
      await axios.delete(`${API}/assignments/${assignmentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchCourseData(token);
    } catch (error) {
      alert(error.response?.data?.detail || "Error deleting assignment");
    }
  };

  const gradeSubmission = async (submissionId, grade, feedback) => {
    const token = localStorage.getItem("token");
    try {
      await axios.patch(`${API}/submissions/${submissionId}/grade`, {
        grade: parseInt(grade),
        feedback
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchCourseData(token);
    } catch (error) {
      alert(error.response?.data?.detail || "Error grading submission");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-transparent flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-transparent">
        <Navbar />
        <div className="pt-24 pb-16 max-w-7xl mx-auto px-4 text-center">
          <AlertCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-700">Course not found</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-transparent" data-testid="course-editor-page">
      <Navbar />
      <div className="pt-20">
        {/* Course Header */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <Link to="/admin" className="inline-flex items-center gap-2 text-purple-200 hover:text-white mb-4">
              <ArrowLeft className="w-4 h-4" />
              Back to Admin
            </Link>
            <div className="flex items-center gap-2 mb-2">
              <Edit className="w-6 h-6" />
              <h1 className="text-2xl sm:text-3xl font-bold">Edit Course</h1>
            </div>
            <p className="text-purple-100 text-xl mb-2">{course.title}</p>
            <p className="text-purple-200 text-sm">{course.description}</p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6">
              <TabsTrigger value="lessons">Lessons ({lessons.length})</TabsTrigger>
              <TabsTrigger value="assignments">Assignments ({assignments.length})</TabsTrigger>
              <TabsTrigger value="submissions">Submissions ({submissions.length})</TabsTrigger>
            </TabsList>

            {/* Lessons Tab */}
            <TabsContent value="lessons">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-slate-700">Course Lessons</h3>
                <Button
                  onClick={() => setShowAddLesson(true)}
                  className="bg-teal-600 hover:bg-teal-700 rounded-full"
                  size="sm"
                  data-testid="add-lesson-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Lesson
                </Button>
              </div>

              {showAddLesson && (
                <Card className="rounded-2xl mb-4 border-2 border-teal-200">
                  <CardContent className="p-6">
                    <form onSubmit={addLesson} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Lesson Title</Label>
                        <Input
                          value={newLesson.title}
                          onChange={(e) => setNewLesson({...newLesson, title: e.target.value})}
                          placeholder="e.g., Introduction to Algebra"
                          className="rounded-xl"
                          data-testid="lesson-title-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Content Type</Label>
                        <select
                          value={newLesson.content_type}
                          onChange={(e) => setNewLesson({...newLesson, content_type: e.target.value})}
                          className="w-full rounded-xl border border-slate-200 px-3 py-2"
                          data-testid="lesson-type-select"
                        >
                          <option value="text">Text</option>
                          <option value="video">Video (YouTube URL)</option>
                          <option value="pdf">PDF (URL)</option>
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label>
                          {newLesson.content_type === "text" ? "Content" : 
                           newLesson.content_type === "video" ? "YouTube URL" : "PDF URL"}
                        </Label>
                        {newLesson.content_type === "text" ? (
                          <Textarea
                            value={newLesson.content}
                            onChange={(e) => setNewLesson({...newLesson, content: e.target.value})}
                            placeholder="Write lesson content here..."
                            className="rounded-xl min-h-[150px]"
                            data-testid="lesson-content-input"
                          />
                        ) : (
                          <Input
                            value={newLesson.content}
                            onChange={(e) => setNewLesson({...newLesson, content: e.target.value})}
                            placeholder={newLesson.content_type === "video" ? "https://youtube.com/watch?v=..." : "https://...pdf"}
                            className="rounded-xl"
                            data-testid="lesson-content-input"
                          />
                        )}
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" onClick={() => setShowAddLesson(false)}>
                          Cancel
                        </Button>
                        <Button type="submit" className="bg-teal-600 hover:bg-teal-700" data-testid="save-lesson-btn">
                          Save Lesson
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              )}

              {lessons.length === 0 ? (
                <Card className="rounded-2xl text-center p-8">
                  <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No lessons yet. Add your first lesson!</p>
                </Card>
              ) : (
                <div className="space-y-3">
                  {lessons.map((lesson, index) => (
                    <Card key={lesson.id} className="rounded-2xl" data-testid={`editor-lesson-${lesson.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                              <span className="font-bold text-teal-600">{index + 1}</span>
                            </div>
                            <div>
                              <h4 className="font-semibold text-slate-800">{lesson.title}</h4>
                              <Badge variant="outline" className="text-xs capitalize">{lesson.content_type}</Badge>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => deleteLesson(lesson.id)}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Assignments Tab */}
            <TabsContent value="assignments">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-slate-700">Course Assignments</h3>
                <Button
                  onClick={() => setShowAddAssignment(true)}
                  className="bg-amber-600 hover:bg-amber-700 rounded-full"
                  size="sm"
                  data-testid="add-assignment-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Assignment
                </Button>
              </div>

              {showAddAssignment && (
                <Card className="rounded-2xl mb-4 border-2 border-amber-200">
                  <CardContent className="p-6">
                    <form onSubmit={addAssignment} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Assignment Title</Label>
                        <Input
                          value={newAssignment.title}
                          onChange={(e) => setNewAssignment({...newAssignment, title: e.target.value})}
                          placeholder="e.g., Chapter 1 Quiz"
                          className="rounded-xl"
                          data-testid="assignment-title-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Description</Label>
                        <Textarea
                          value={newAssignment.description}
                          onChange={(e) => setNewAssignment({...newAssignment, description: e.target.value})}
                          placeholder="Describe the assignment..."
                          className="rounded-xl min-h-[100px]"
                          data-testid="assignment-description-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Due Date (Optional)</Label>
                        <Input
                          type="date"
                          value={newAssignment.due_date}
                          onChange={(e) => setNewAssignment({...newAssignment, due_date: e.target.value})}
                          className="rounded-xl"
                          data-testid="assignment-due-date-input"
                        />
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" onClick={() => setShowAddAssignment(false)}>
                          Cancel
                        </Button>
                        <Button type="submit" className="bg-amber-600 hover:bg-amber-700" data-testid="save-assignment-btn">
                          Save Assignment
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              )}

              {assignments.length === 0 ? (
                <Card className="rounded-2xl text-center p-8">
                  <ClipboardList className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No assignments yet. Add your first assignment!</p>
                </Card>
              ) : (
                <div className="space-y-3">
                  {assignments.map((assignment) => (
                    <Card key={assignment.id} className="rounded-2xl" data-testid={`editor-assignment-${assignment.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-semibold text-slate-800">{assignment.title}</h4>
                            <p className="text-sm text-slate-500 mt-1">{assignment.description}</p>
                            {assignment.due_date && (
                              <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
                                <Calendar className="w-3 h-3" />
                                <span>Due: {new Date(assignment.due_date).toLocaleDateString()}</span>
                              </div>
                            )}
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => deleteAssignment(assignment.id)}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Submissions Tab */}
            <TabsContent value="submissions">
              <h3 className="font-semibold text-slate-700 mb-4">Student Submissions</h3>
              
              {submissions.length === 0 ? (
                <Card className="rounded-2xl text-center p-8">
                  <CheckSquare className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No submissions yet</p>
                </Card>
              ) : (
                <div className="space-y-3">
                  {submissions.map((submission) => (
                    <SubmissionCard 
                      key={submission.id} 
                      submission={submission} 
                      onGrade={gradeSubmission}
                    />
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

// ============ SUBMISSION CARD (for grading) ============
const SubmissionCard = ({ submission, onGrade }) => {
  const [showGrading, setShowGrading] = useState(false);
  const [grade, setGrade] = useState(submission.grade || "");
  const [feedback, setFeedback] = useState(submission.feedback || "");

  const handleGrade = () => {
    if (!grade || isNaN(grade) || grade < 0 || grade > 100) {
      alert("Please enter a valid grade between 0 and 100");
      return;
    }
    onGrade(submission.id, grade, feedback);
    setShowGrading(false);
  };

  return (
    <Card className="rounded-2xl" data-testid={`submission-${submission.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                <User className="w-4 h-4 text-purple-600" />
              </div>
              <div>
                <span className="font-medium text-slate-800">{submission.student_name}</span>
                {submission.student_district && (
                  <Badge variant="outline" className="ml-2 text-xs">{submission.student_district}</Badge>
                )}
              </div>
            </div>
            <p className="text-sm text-slate-500 mb-1">
              <span className="font-medium">Assignment:</span> {submission.assignment_title}
            </p>
            <div className="bg-slate-50 p-3 rounded-xl mt-2">
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{submission.content}</p>
            </div>
            <p className="text-xs text-slate-400 mt-2">
              Submitted: {new Date(submission.submitted_at).toLocaleString()}
            </p>
          </div>
          <div className="text-right">
            {submission.grade !== null ? (
              <div className="flex flex-col items-end">
                <Badge className={`${
                  submission.grade >= 70 ? 'bg-green-100 text-green-700' :
                  submission.grade >= 50 ? 'bg-amber-100 text-amber-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  {submission.grade}/100
                </Badge>
                <Button
                  size="sm"
                  variant="link"
                  onClick={() => setShowGrading(true)}
                  className="text-xs"
                >
                  Edit Grade
                </Button>
              </div>
            ) : (
              <Button
                size="sm"
                onClick={() => setShowGrading(true)}
                className="bg-purple-600 hover:bg-purple-700 rounded-full"
                data-testid={`grade-${submission.id}-btn`}
              >
                <Star className="w-4 h-4 mr-1" />
                Grade
              </Button>
            )}
          </div>
        </div>

        {showGrading && (
          <div className="mt-4 pt-4 border-t">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Grade (0-100)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={grade}
                  onChange={(e) => setGrade(e.target.value)}
                  className="rounded-xl"
                  data-testid="grade-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Feedback (Optional)</Label>
                <Input
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="Great work!"
                  className="rounded-xl"
                  data-testid="feedback-input"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setShowGrading(false)} size="sm">
                Cancel
              </Button>
              <Button onClick={handleGrade} className="bg-purple-600 hover:bg-purple-700" size="sm" data-testid="save-grade-btn">
                Save Grade
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ============ APP ============
function App() {
  return (
    <div className="App min-h-screen relative">
      {/* Global Bamboo Background */}
      <div 
        className="fixed inset-0 bg-cover bg-center bg-no-repeat -z-10"
        style={{ backgroundImage: "url('/images/bamboo-bg.avif')" }}
      ></div>
      <div className="fixed inset-0 bg-white/80 -z-10"></div>
      
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/enrollment" element={<Enrollment />} />
          <Route path="/dream-option" element={<DreamOption />} />
          <Route path="/chat" element={<Chat />} />
          <Route path="/competitions" element={<Competitions />} />
          <Route path="/activities" element={<Activities />} />
          <Route path="/duna100" element={<Duna100 />} />
          <Route path="/payment" element={<Payment />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/courses" element={<Courses />} />
          <Route path="/courses/:courseId" element={<CourseDetail />} />
          <Route path="/courses/:courseId/edit" element={<CourseEditor />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
